import { Footer } from '../components/Footer';
import TestBanner from '../components/TestBanner';
import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Label } from '@/components/ui/label';
import { UserCheck, Calendar, CalendarClock, Search, CheckCircle2, Circle, Plus, Users, Music, Guitar, BarChart3, UserRound, Menu, LogOut, Rows3, Baby, Flower2, Cake, User, ClipboardCheck, Piano, Music2, AlertTriangle, Edit, StickyNote, Trash2, Save, ListFilter, Lock, Vote, MessageSquare, Loader2, BookOpen, Link, Zap, X, Info, ChevronDown, ChevronRight, UserX, Check, Star, Hash, HelpCircle, Package, Settings, Droplets, Download, Upload } from 'lucide-react';
import DashboardTab from '@/components/admin/DashboardTab';
import RelatoriosTab from '@/components/admin/RelatoriosTab';
import AniversariantesTab from '@/components/admin/AniversariantesTab';
import AuxiliarVotacoesTab from './AuxiliarVotacoesTab';
import AuxiliarReunioesTab from '@/components/AuxiliarReunioesTab';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { useSettings } from '@/lib/useSettings';
import { formatPhone, EMAIL_REGEX, INSTRUMENTOS, norm, calcAgeNum } from '@/lib/helpers';
import { useNotifications } from '@/hooks/useNotifications';
import { NotificationBanner, NotificationBellIcon, NotificationModal } from '@/components/NotificationBell';
import EscalasAuxView from '@/components/EscalasAuxView';
import HelpWizardModal from '@/components/HelpWizardModal';
import CestasBasicasTab from '@/components/CestasBasicasTab';
import BatismosTab from '@/components/BatismosTab';
import { AccessShortcutButton } from '@/components/AccessShortcutButton';
import AddressAutocompleteInput from '@/components/AddressAutocompleteInput';
import { emptyAddressFields, parseGooglePlaceAddress } from '@/lib/address';

const API = `${window.location.origin}/api`;
const BACKEND_URL = window.location.origin;

function fmtDate(dateStr) {
  if (!dateStr) return '';
  let datePart = dateStr;
  let timePart = '';
  if (dateStr.includes('T')) {
    [datePart, timePart] = dateStr.split('T');
  } else if (dateStr.includes(' ')) {
    [datePart, timePart] = dateStr.split(' ');
  }
  const parts = datePart.split('-');
  if (parts.length === 3) {
    const formatted = `${parts[2]}/${parts[1]}/${parts[0]}`;
    return timePart ? `${formatted} ${timePart.slice(0, 5)}` : formatted;
  }
  return dateStr;
}

function calcAge(dobStr) {
  if (!dobStr) return null;
  const [y, m, d] = dobStr.split('-').map(Number);
  const today = new Date();
  let age = today.getFullYear() - y;
  if (today.getMonth() + 1 < m || (today.getMonth() + 1 === m && today.getDate() < d)) age--;
  return age;
}

function calcAgeLabel(dobStr) {
  const age = calcAge(dobStr);
  return age !== null ? `${age} anos` : '';
}

function getAvatarColor(pessoa) {
  return pessoa.genero === 'feminino' ? '#EC4899' : '#3B82F6';
}

function photoFrameStyle(pessoa = {}) {
  return { objectPosition: `${Number(pessoa.foto_pos_x ?? 50)}% ${Number(pessoa.foto_pos_y ?? 50)}%`, transform: `scale(${Number(pessoa.foto_zoom ?? 1)})`, transformOrigin: `${Number(pessoa.foto_pos_x ?? 50)}% ${Number(pessoa.foto_pos_y ?? 50)}%` };
}

function formatPhotoSize(size) {
  const bytes = Number(size || 0);
  if (!bytes) return 'Tamanho não disponível';
  if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1).replace('.', ',')} MB`;
  return `${Math.max(1, Math.round(bytes / 1024))} KB`;
}

function PessoaAvatar({ pessoa, className = 'w-8 h-8 sm:w-10 sm:h-10', fallbackClassName = '', testId = 'pessoa-avatar', preferThumb = true }) {
  const imageUrl = preferThumb ? (pessoa?.foto_thumb_url || pessoa?.foto_url) : pessoa?.foto_url;
  if (imageUrl) return <span className={`${className} rounded-full overflow-hidden border-2 border-white shadow-sm flex-shrink-0 bg-slate-100 inline-flex`} data-testid={testId}><img src={`${BACKEND_URL}${imageUrl}`} alt={`Foto de ${pessoa.nome_completo || 'pessoa'}`} className="h-full w-full object-cover" style={photoFrameStyle(pessoa)} loading="lazy" decoding="async" /></span>;
  return <div className={`${className} rounded-full flex items-center justify-center font-bold text-xs sm:text-sm flex-shrink-0 text-white ${fallbackClassName}`} style={fallbackClassName ? undefined : { backgroundColor: getAvatarColor(pessoa || {}) }} data-testid={`${testId}-fallback`}>{(pessoa?.nome_completo || '?').charAt(0)}</div>;
}

function PhotoFrameControls({ form, setForm, onAutoSave, prefix = 'aux-pessoa' }) {
  const update = (field, value) => {
    const next = { ...form, [field]: Number(value) };
    setForm(next);
    onAutoSave?.(next);
  };
  return <div className="mt-2 rounded-lg border border-slate-200 bg-white/80 p-2" data-testid={`${prefix}-photo-frame-controls`}>
    <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
      <label className="space-y-0.5"><span className="text-[10px] font-bold uppercase text-slate-500">Horizontal</span><Input className="h-6 px-0" type="range" min="0" max="100" value={form.foto_pos_x ?? 50} onChange={e => update('foto_pos_x', e.target.value)} data-testid={`${prefix}-photo-pos-x`} /></label>
      <label className="space-y-0.5"><span className="text-[10px] font-bold uppercase text-slate-500">Vertical</span><Input className="h-6 px-0" type="range" min="0" max="100" value={form.foto_pos_y ?? 50} onChange={e => update('foto_pos_y', e.target.value)} data-testid={`${prefix}-photo-pos-y`} /></label>
      <label className="space-y-0.5"><span className="text-[10px] font-bold uppercase text-slate-500">Zoom</span><Input className="h-6 px-0" type="range" min="1" max="2" step="0.05" value={form.foto_zoom ?? 1} onChange={e => update('foto_zoom', e.target.value)} data-testid={`${prefix}-photo-zoom`} /></label>
    </div>
  </div>;
}

export default function AuxiliarDashboard() {
  const navigate = useNavigate();
  const { settings } = useSettings();
  const [user, setUser] = useState(null);
  const [auxiliar, setAuxiliar] = useState(null);
  const [appSettings, setAppSettings] = useState({});
  const [pessoas, setPessoas] = useState([]);
  const [allAuxiliares, setAllAuxiliares] = useState([]);
  const [events, setEvents] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [attendance, setAttendance] = useState({});
  const [attendanceMeta, setAttendanceMeta] = useState({}); // { pessoa_id: { marked_at, marked_by } }
  const [recentChangeConfirm, setRecentChangeConfirm] = useState(null); // { pessoaId, desiredPresente, markedBy, markedAt }
  const [absentLast5, setAbsentLast5] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const searchAttRef = useRef(null);
  const [quickMode, setQuickMode] = useState(false);
  const [eventDialog, setEventDialog] = useState(false);
  const [eventForm, setEventForm] = useState({ name: '', date: '', description: '', tipo_evento_id: '', quem_presidiu: '', qtd_musicos: '', recitativos: {}, qtd_ministerio: '', qtd_participantes: '', pessoa_id: '', pessoa_nome: '', pessoa_foto_url: '' });
  const [saving, setSaving] = useState(false);
  const [initError, setInitError] = useState(null);
  const [filterMode, setFilterMode] = useState('minhas');
  const [activeTab, setActiveTab] = useState('dashboard');
  const [managementMenuOpen, setManagementMenuOpen] = useState(false);
  const [managementMenuPinnedClosed, setManagementMenuPinnedClosed] = useState(false);
  const [frequencyReport, setFrequencyReport] = useState([]);
  const [auxiliarReport, setAuxiliarReport] = useState([]);
  const [eventsEngagement, setEventsEngagement] = useState([]);
  const [pessoasStats, setPessoasStats] = useState([]);
  const [tiposEvento, setTiposEvento] = useState([]);
  const [pessoaFilterMode, setPessoaFilterMode] = useState('minhas');
  const [pessoaSearch, setPessoaSearch] = useState('');
  const [auxPessoaSearch, setAuxPessoaSearch] = useState('');
  const [auxPessoaDropdown, setAuxPessoaDropdown] = useState(false);
  const [pessoaDialog, setPessoaDialog] = useState(false);
  const [editingPessoa, setEditingPessoa] = useState(null);
  const [pessoaForm, setPessoaForm] = useState({ nome_completo: '', genero: '', data_nascimento: '', email: '', whatsapp: '', whatsapp_pai: '', whatsapp_mae: '', foto_url: '', foto_thumb_url: '', foto_size: null, foto_content_type: '', foto_thumb_size: null, foto_thumb_content_type: '', foto_pos_x: 50, foto_pos_y: 50, foto_zoom: 1, ...emptyAddressFields(), batizado: false, selado: false, nome_pai: '', nome_mae: '', auxiliar_jovens: false, grupo_visita: false, organista: false, instrutora_musicalizacao: false, integrante_ebi: false, musico: false, instrumentos: [], instrutor_gem: false, aluno_gem: false });
  const pessoaPhotoInputRef = useRef(null);
  const [pessoaPhotoUploading, setPessoaPhotoUploading] = useState(false);
  const [photoOverrides, setPhotoOverrides] = useState({});
  const photoFrameSaveTimerRef = useRef(null);
  const [pessoaErrors, setPessoaErrors] = useState({});
  const [anotacoes, setAnotacoes] = useState([]);
  const [anotLoading, setAnotLoading] = useState(false);
  const [anotDialog, setAnotDialog] = useState(false);
  const [editingAnot, setEditingAnot] = useState(null);
  const [anotForm, setAnotForm] = useState({ descricao: '', data: '' });
  const [pessoaEditTab, setPessoaEditTab] = useState('info');
  const [pessoaViewMode, setPessoaViewMode] = useState('edit'); // 'edit' or 'anotacoes'

  // Close Event state
  const [closeDialog, setCloseDialog] = useState(false);
  const [closeForm, setCloseForm] = useState({ quem_presidiu: '', qtd_musicos: '', recitativos: {}, qtd_ministerio: '', qtd_participantes: '' });

  // Notifications
  const { notifications, dismissed, dismiss: dismissNotifications, refresh: refreshNotifications, fetchNotifications } = useNotifications();
  const [bellOpen, setBellOpen] = useState(false);
  const bellRef = useRef(null);
  const managementMenuCloseTimer = useRef(null);
  const managementMenuRef = useRef(null);

  const [isImpersonating, setIsImpersonating] = useState(false);

  // My Schedules modal
  const [mySchedulesOpen, setMySchedulesOpen] = useState(false);
  const [mySchedules, setMySchedules] = useState([]);
  const [mySchedulesLoading, setMySchedulesLoading] = useState(false);
  const [expandedEvent, setExpandedEvent] = useState(null);
  const [absenceConfirm, setAbsenceConfirm] = useState(null);
  const [logModal, setLogModal] = useState(null);
  const [escalasViewOpen, setEscalasViewOpen] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);

  // Service functions for "minhas pessoas" grouping
  const [serviceFunctions, setServiceFunctions] = useState([]);

  // Contagem modal state
  const [contagemDialog, setContagemDialog] = useState(false);
  const [contagemForm, setContagemForm] = useState({});
  const [contagemSaving, setContagemSaving] = useState(false);
  const [birthdayCalendarDialog, setBirthdayCalendarDialog] = useState(false);
  const [birthdayCalendarUrl, setBirthdayCalendarUrl] = useState('');
  const [birthdayCalendarLoading, setBirthdayCalendarLoading] = useState(false);
  const [eventsSidebarOpen, setEventsSidebarOpen] = useState(false);
  const [reopenDialog, setReopenDialog] = useState(false);

  useEffect(() => { initDashboard(); }, []);

  // Polling attendance every 1s for real-time sync
  useEffect(() => {
    if (!selectedEvent || activeTab !== 'presenca') return;
    const interval = setInterval(async () => {
      try {
        const r = await fetch(`${API}/attendance/event/${selectedEvent.event_id}/poll`, { credentials: 'include' });
        if (!r.ok) return;
        const data = await r.json();
        const newAtt = {};
        const newMeta = {};
        for (const [pid, info] of Object.entries(data)) {
          newAtt[pid] = info.presente;
          newMeta[pid] = { marked_at: info.marked_at, marked_by: info.marked_by };
        }
        setAttendance(prev => {
          const changed = Object.keys(newAtt).some(k => prev[k] !== newAtt[k]) || Object.keys(prev).some(k => prev[k] !== newAtt[k]);
          return changed ? newAtt : prev;
        });
        setAttendanceMeta(newMeta);
      } catch (err) { console.warn('Falha ao atualizar presenças em tempo real', err); }
    }, 1000);
    return () => clearInterval(interval);
  }, [selectedEvent, activeTab]);

  // Refresh data and notifications when switching tabs
  useEffect(() => {
    if (activeTab === 'presenca') initDashboard();
    fetchNotifications();
  }, [activeTab]);

  useEffect(() => {
    if (!selectedEvent && events && events.length > 0) {
      const now = new Date();
      now.setHours(23, 59, 59, 999);
      const openPastEvents = events.filter(e => e.status === 'aberto' && new Date(e.date) <= now);
      if (openPastEvents.length > 0) {
        openPastEvents.sort((a, b) => new Date(b.date) - new Date(a.date));
        setSelectedEvent(openPastEvents[0]);
      }
    }
  }, [events]);

  useEffect(() => {
    if (selectedEvent) fetchEventAttendance(selectedEvent.event_id);
  }, [selectedEvent]);

  useEffect(() => {
    if ((activeTab === 'votacoes' && settings?.addons && !settings.addons.votacoes) || (activeTab === 'reunioes' && settings?.addons && !settings.addons.reunioes) || (activeTab === 'cestas_basicas' && settings?.addons && !settings.addons.cestas_basicas) || (activeTab === 'batismos' && settings?.addons && !settings.addons.batismos)) {
      setActiveTab('dashboard');
    }
  }, [activeTab, settings?.addons]);

  useEffect(() => {
    if (!managementMenuOpen) return;
    const handleOutsideClick = (event) => {
      if (managementMenuRef.current && !managementMenuRef.current.contains(event.target)) {
        setManagementMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, [managementMenuOpen]);

  const initDashboard = async () => {
    try {
      // Check if this is an impersonated session
      const userResp = await fetch(`${API}/auth/me`, { credentials: 'include' });
      if (!userResp.ok) { navigate('/'); return; }
      const userData = await userResp.json();
      if (userData.must_change_password) { navigate('/alterar-senha'); return; }
      if (userData.role === 'ebi_coordenadora') { navigate('/ebi'); return; }
      if (userData.role === 'movimentos_coordenador') { navigate('/movimentos-painel'); return; }
      if (userData.role === 'admin') { navigate('/admin'); return; }
      if (userData.role === 'gestor') { navigate('/gestor'); return; }
      if (userData.impersonated) setIsImpersonating(true);
      setUser(userData);

      const auxResp = await fetch(`${API}/auxiliares/me`, { credentials: 'include' });
      if (!auxResp.ok) {
        setInitError('Perfil de auxiliar não encontrado. Contacte o administrador.');
        return;
      }
      const auxData = await auxResp.json();
      setAuxiliar(auxData);

      const [pessoasResp, eventsResp, auxListResp, tiposResp, settingsResp] = await Promise.all([
        fetch(`${API}/pessoas?status=aprovado`, { credentials: 'include' }),
        fetch(`${API}/events`, { credentials: 'include' }),
        fetch(`${API}/auxiliares`, { credentials: 'include' }),
        fetch(`${API}/tipos-evento`, { credentials: 'include' }),
        fetch(`${API}/settings`, { credentials: 'include' }),
      ]);
      if (pessoasResp.ok) setPessoas(await pessoasResp.json());
      if (eventsResp.ok) {
        const eventsData = await eventsResp.json();
        setEvents(eventsData.filter(e => e.status === 'aberto').sort((a, b) => new Date(b.date) - new Date(a.date)));
      }
      if (auxListResp.ok) setAllAuxiliares(await auxListResp.json());
      if (tiposResp.ok) setTiposEvento(await tiposResp.json());
      if (settingsResp.ok) setAppSettings(await settingsResp.json());

      fetch(`${API}/attendance/absent-last-5`, { credentials: 'include' }).then(r => r.ok ? r.json() : []).then(setAbsentLast5).catch(() => {});
      // Check recurring events
      fetch(`${API}/events/check-recorrencia`, { method: 'POST', credentials: 'include' }).catch(() => {});
      // Service functions for "minhas pessoas"
      fetch(`${API}/service-functions`, { credentials: 'include' }).then(r => r.ok ? r.json() : []).then(setServiceFunctions).catch(() => {});
      // Load my schedules for presença header functions
      fetch(`${API}/service-schedules/my-schedules?_t=${Date.now()}`, { credentials: 'include', cache: 'no-store' }).then(r => r.ok ? r.json() : []).then(setMySchedules).catch(() => {});

      const [freqResp, auxRepResp, evEngResp, pesStatsResp, pergResp] = await Promise.all([
        fetch(`${API}/reports/frequency`, { credentials: 'include' }),
        fetch(`${API}/reports/by-auxiliar`, { credentials: 'include' }),
        fetch(`${API}/reports/events-engagement`, { credentials: 'include' }),
        fetch(`${API}/pessoas/stats`, { credentials: 'include' }),
      ]);
      if (freqResp.ok) setFrequencyReport(await freqResp.json());
      if (auxRepResp.ok) setAuxiliarReport(await auxRepResp.json());
      if (evEngResp.ok) setEventsEngagement(await evEngResp.json());
      if (pesStatsResp.ok) setPessoasStats(await pesStatsResp.json());
    } catch (error) {
      navigate('/');
    }
  };

  const fetchEventAttendance = async (eventId) => {
    try {
      setLoading(true);
      const response = await fetch(`${API}/attendance/event/${eventId}`, { credentials: 'include' });
      if (!response.ok) return;
      const data = await response.json();
      const map = {};
      data.forEach(att => { map[att.pessoa_id] = att.presente; });
      setAttendance(map);
    } catch (error) {
      console.error('Error fetching attendance:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try { await fetch(`${API}/auth/logout`, { method: 'POST', credentials: 'include' }); } catch (e) { console.warn('Falha ao sair', e); }
    navigate('/');
  };

  const downloadContactsVcf = () => {
    const a = document.createElement('a');
    a.href = `${API}/pessoas/contacts-vcf`;
    a.download = 'contatos.vcf';
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  const openBirthdayCalendarDialog = async () => {
    setBirthdayCalendarDialog(true);
    setBirthdayCalendarLoading(true);
    try {
      const response = await fetch(`${API}/pessoas/birthday-calendar-link`, { credentials: 'include' });
      if (!response.ok) throw new Error('Não foi possível gerar o link da agenda.');
      const data = await response.json();
      setBirthdayCalendarUrl(data.url || '');
    } catch (error) {
      toast.error(error.message || 'Erro ao gerar link da agenda.');
    } finally {
      setBirthdayCalendarLoading(false);
    }
  };

  const copyBirthdayCalendarUrl = async () => {
    try {
      await navigator.clipboard.writeText(birthdayCalendarUrl);
      toast.success('Link da agenda copiado!');
    } catch {
      toast.error('Não foi possível copiar o link.');
    }
  };

  const openManagementMenu = () => {
    if (managementMenuCloseTimer.current) clearTimeout(managementMenuCloseTimer.current);
    if (managementMenuPinnedClosed) return;
    setManagementMenuOpen(true);
  };

  const scheduleCloseManagementMenu = () => {
    if (managementMenuCloseTimer.current) clearTimeout(managementMenuCloseTimer.current);
    managementMenuCloseTimer.current = setTimeout(() => setManagementMenuOpen(false), 160);
  };

  const selectAuxTab = (key) => {
    setActiveTab(key);
    setManagementMenuOpen(false);
  };

  const handleToggleAttendance = async (pessoaId, desiredPresente) => {
    if (!selectedEvent) return;
    // Check if someone else changed this in the last 30s
    const meta = attendanceMeta[pessoaId];
    if (meta && meta.marked_at && meta.marked_by) {
      const myId = auxiliar?.auxiliar_id || auxiliar?.user_id;
      if (meta.marked_by !== myId) {
        const markedTime = new Date(meta.marked_at);
        const now = new Date();
        const diffSec = (now - markedTime) / 1000;
        if (diffSec < 30) {
          // Find who marked it
          const markerName = allAuxiliares.find(a => a.user_id === meta.marked_by)?.name || 'outro auxiliar';
          setRecentChangeConfirm({ pessoaId, desiredPresente, markedBy: markerName, secondsAgo: Math.round(diffSec) });
          return;
        }
      }
    }
    await executeToggleAttendance(pessoaId, desiredPresente);
  };

  const executeToggleAttendance = async (pessoaId, desiredPresente) => {
    if (!selectedEvent) return;
    try {
      const response = await fetch(`${API}/attendance`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ event_id: selectedEvent.event_id, pessoa_id: pessoaId, presente: desiredPresente })
      });
      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.detail || 'Erro ao marcar presença');
      }
      setAttendance(prev => ({ ...prev, [pessoaId]: desiredPresente }));
      toast.success(desiredPresente ? 'Presença marcada!' : 'Ausência marcada!');
      if (quickMode) {
        setSearchTerm('');
        setTimeout(() => searchAttRef.current?.focus(), 50);
      }
    } catch (error) {
      toast.error(error.message);
    }
  };

  const selectedTipoEvento = tiposEvento.find(t => t.tipo_evento_id === eventForm.tipo_evento_id);

  const buildRecitativosPayloadAux = () => {
    const rec = eventForm.recitativos || {};
    return { individual_meninos: rec.individual_meninos !== '' ? parseInt(rec.individual_meninos) : null, individual_meninas: rec.individual_meninas !== '' ? parseInt(rec.individual_meninas) : null };
  };

  const handleCreateEvent = async () => {
    if (!eventForm.tipo_evento_id && tiposEvento.length > 0) { toast.error('Selecione o tipo de evento'); return; }
    if (!eventForm.name || !eventForm.date) { toast.error('Preencha nome e data do evento'); return; }
    setSaving(true);
    try {
      const payload = {
        ...eventForm,
        qtd_musicos: eventForm.qtd_musicos !== '' ? parseInt(eventForm.qtd_musicos) : null,
        qtd_ministerio: eventForm.qtd_ministerio !== '' ? parseInt(eventForm.qtd_ministerio) : null,
        qtd_participantes: eventForm.qtd_participantes !== '' ? parseInt(eventForm.qtd_participantes) : null,
        recitativos: selectedTipoEvento?.tem_recitativos ? buildRecitativosPayloadAux() : null,
        tipo_evento_id: eventForm.tipo_evento_id || null,
        visibilidade: 'auxiliares',
      };
      const response = await fetch(`${API}/events`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(payload)
      });
      if (!response.ok) throw new Error('Erro ao criar evento');
      const newEvent = await response.json();
      setEvents(prev => [newEvent, ...prev]);
      setEventDialog(false);
      setEventForm({ name: '', date: '', description: '', tipo_evento_id: '', quem_presidiu: '', qtd_musicos: '', recitativos: {}, qtd_ministerio: '', qtd_participantes: '', pessoa_id: '', pessoa_nome: '', pessoa_foto_url: '' });
      setAuxPessoaSearch('');
      toast.success('Evento criado!');
      fetchNotifications();
    } catch (error) {
      toast.error(error.message);
    } finally { setSaving(false); }
  };

  // Close Event logic
  const selectedTipoClose = tiposEvento.find(t => t.tipo_evento_id === selectedEvent?.tipo_evento_id);

  // State for recitativos check in close dialog
  const [closeRecStatus, setCloseRecStatus] = useState({ complete: true, missingGenders: [] });
  const [closeSaving, setCloseSaving] = useState(false);

  const openCloseDialog = async () => {
    // Fetch latest event data
    let latestEvent = selectedEvent;
    try {
      const r = await fetch(`${API}/events/${selectedEvent.event_id}`, { credentials: 'include' });
      if (r.ok) latestEvent = await r.json();
    } catch (err) { console.warn('Falha ao buscar evento atualizado', err); }

    const cf = {
      quem_presidiu: latestEvent?.quem_presidiu || '',
      qtd_musicos: latestEvent?.qtd_musicos ?? '',
      qtd_ministerio: latestEvent?.qtd_ministerio ?? '',
      qtd_participantes: latestEvent?.qtd_participantes ?? '',
      recitativos: {},
    };
    setCloseForm(cf);

    // Check recitativos completeness
    if (selectedTipoClose?.tem_recitativos) {
      const eventRec = latestEvent?.recitativos || {};

      // Fetch fresh service functions and schedule in parallel
      let allFuncs = serviceFunctions;
      let schedule = null;
      try {
        const [funcsR, schedR] = await Promise.all([
          fetch(`${API}/service-functions`, { credentials: 'include' }),
          fetch(`${API}/service-schedules/${selectedEvent.event_id}`, { credentials: 'include' })
        ]);
        if (funcsR.ok) allFuncs = await funcsR.json();
        if (schedR.ok) schedule = await schedR.json();
      } catch (err) { console.warn('Falha ao buscar recitativos da escala', err); }

      // Build rec functions list (same logic as admin)
      const allRecFuncs = (allFuncs || []).filter(f => f.active !== false && (
        f.tipo === 'recitativo' || (f.tipo === 'outros' && (f.nome || '').toLowerCase().includes('recitativo individual'))
      ));

      // Find Contador/Contadora functions
      const contadorFuncs = (allFuncs || []).filter(f => (f.nome || '').toLowerCase().includes('contador'));

      const missingGenders = [];
      for (const gender of ['feminino', 'masculino']) {
        const genderFuncs = allRecFuncs.filter(f => (f.eligibility?.gender || 'todos') === gender);
        if (genderFuncs.length === 0) continue;

        const missingFuncs = genderFuncs.filter(f => eventRec[f.function_id] === undefined || eventRec[f.function_id] === null || eventRec[f.function_id] === '');
        if (missingFuncs.length === 0) continue;

        // Find Contador for this gender
        let contadorName = null;
        const genderContador = contadorFuncs.find(f => (f.eligibility?.gender || '') === gender);
        if (genderContador && schedule?.assignments) {
          const assignment = schedule.assignments.find(a => a.function_id === genderContador.function_id);
          if (assignment) {
            const pids = gender === 'masculino' ? (assignment.male || []) : (assignment.female || []);
            const names = pids.map(pid => { const p = pessoas.find(pp => pp.pessoa_id === pid); return p ? (p.apelido || p.nome_completo?.split(' ')[0]) : null; }).filter(Boolean);
            if (names.length > 0) contadorName = names.join(', ');
          }
        }

        missingGenders.push({
          gender,
          label: gender === 'feminino' ? 'Meninas' : 'Meninos',
          contadorName,
          missingFuncNames: missingFuncs.map(f => f.nome)
        });
      }
      setCloseRecStatus({ complete: missingGenders.length === 0, missingGenders });
    } else {
      setCloseRecStatus({ complete: true, missingGenders: [] });
    }

    setCloseDialog(true);
  };

  const handleSaveAdditionalInfo = async () => {
    setCloseSaving(true);
    try {
      const { recitativos, ...eventWithoutRec } = selectedEvent;
      const payload = {
        ...eventWithoutRec,
        quem_presidiu: closeForm.quem_presidiu,
        qtd_musicos: closeForm.qtd_musicos !== '' ? parseInt(closeForm.qtd_musicos) : null,
        qtd_ministerio: closeForm.qtd_ministerio !== '' ? parseInt(closeForm.qtd_ministerio) : null,
        qtd_participantes: closeForm.qtd_participantes !== '' ? parseInt(closeForm.qtd_participantes) : null,
      };
      const res = await fetch(`${API}/events/${selectedEvent.event_id}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error('Erro ao salvar');
      const updated = await res.json();
      setEvents(prev => prev.map(e => e.event_id === updated.event_id ? updated : e));
      setSelectedEvent(updated);
      toast.success('Informações salvas!');
    } catch (err) { toast.error(err.message); } finally { setCloseSaving(false); }
  };

  const handleReopenEvent = async () => {
    try {
      const { recitativos: _rec, ...eventWithoutRec } = selectedEvent;
      const payload = { ...eventWithoutRec, status: 'aberto' };
      const res = await fetch(`${API}/events/${selectedEvent.event_id}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error('Erro ao reabrir evento');
      const updated = await res.json();
      setEvents(prev => prev.map(e => e.event_id === updated.event_id ? updated : e));
      setSelectedEvent(updated);
      setReopenDialog(false);
      toast.success('Evento reaberto!');
    } catch (err) { toast.error(err.message); }
  };

  const validateCloseForm = () => {
    const missing = [];
    if (selectedTipoClose?.tem_presidencia && !(closeForm.quem_presidiu || '').trim()) missing.push('Quem Presidiu');
    if (selectedTipoClose?.tem_musicos && (closeForm.qtd_musicos === '' || closeForm.qtd_musicos === null)) missing.push('Qtd. de Músicos');
    if (selectedTipoClose?.tem_ministerio && (closeForm.qtd_ministerio === '' || closeForm.qtd_ministerio === null)) missing.push('Qtd. de Ministério');
    if (selectedTipoClose?.tem_participantes && (closeForm.qtd_participantes === '' || closeForm.qtd_participantes === null)) missing.push('Qtd. de Participantes');
    if (selectedTipoClose?.tem_recitativos) {
      const rec = closeForm.recitativos || {};
      if (rec.individual_meninos === '' || rec.individual_meninos === undefined) missing.push('Individual Meninos');
      if (rec.individual_meninas === '' || rec.individual_meninas === undefined) missing.push('Individual Meninas');
    }
    return missing;
  };

  const buildCloseRecitativosPayload = () => {
    const rec = closeForm.recitativos || {};
    return { individual_meninos: rec.individual_meninos !== '' ? parseInt(rec.individual_meninos) : null, individual_meninas: rec.individual_meninas !== '' ? parseInt(rec.individual_meninas) : null };
  };

  const getCloseDeadlineInfo = () => {
    if (!selectedEvent || !selectedTipoClose?.fechamento_valor || !selectedEvent.date) return { canClose: true, label: '' };
    try {
      const eventDate = new Date(selectedEvent.date);
      if (isNaN(eventDate.getTime())) return { canClose: true, label: '' };
      const delta = selectedTipoClose.fechamento_unidade === 'dias' ? selectedTipoClose.fechamento_valor * 24 * 60 * 60 * 1000 : selectedTipoClose.fechamento_valor * 60 * 60 * 1000;
      const deadline = new Date(eventDate.getTime() + delta);
      const now = new Date();
      if (now >= deadline) return { canClose: true, label: '' };
      const diffMs = deadline - now;
      const diffH = Math.ceil(diffMs / (1000 * 60 * 60));
      const label = diffH >= 24 ? `Podera ser fechado em ${Math.ceil(diffH / 24)} dia(s)` : `Podera ser fechado em ${diffH} hora(s)`;
      return { canClose: false, label };
    } catch { return { canClose: true, label: '' }; }
  };

  const handleCloseEventSubmit = async () => {
    if (!closeRecStatus.complete) { toast.error('Aguardando contagem de recitativos.'); return; }
    const missing = [];
    if (selectedTipoClose?.tem_presidencia && !(closeForm.quem_presidiu || '').trim()) missing.push('Quem Presidiu');
    if (selectedTipoClose?.tem_musicos && (closeForm.qtd_musicos === '' || closeForm.qtd_musicos === null)) missing.push('Qtd. de Músicos');
    if (selectedTipoClose?.tem_ministerio && (closeForm.qtd_ministerio === '' || closeForm.qtd_ministerio === null)) missing.push('Qtd. de Ministério');
    if (selectedTipoClose?.tem_participantes && (closeForm.qtd_participantes === '' || closeForm.qtd_participantes === null)) missing.push('Qtd. de Participantes');
    if (missing.length > 0) { toast.error(`Preencha: ${missing.join(', ')}`); return; }
    try {
      const { recitativos: _rec, ...eventWithoutRec } = selectedEvent;
      const payload = {
        ...eventWithoutRec,
        status: 'fechado',
        quem_presidiu: closeForm.quem_presidiu,
        qtd_musicos: closeForm.qtd_musicos !== '' ? parseInt(closeForm.qtd_musicos) : null,
        qtd_ministerio: closeForm.qtd_ministerio !== '' ? parseInt(closeForm.qtd_ministerio) : null,
        qtd_participantes: closeForm.qtd_participantes !== '' ? parseInt(closeForm.qtd_participantes) : null,
      };
      const res = await fetch(`${API}/events/${selectedEvent.event_id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error('Erro ao fechar evento');
      const updated = await res.json();
      setEvents(prev => prev.map(e => e.event_id === updated.event_id ? updated : e));
      setSelectedEvent(updated);
      setCloseDialog(false);
      toast.success('Evento fechado!');
      fetchNotifications();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const openEditPessoa = (p) => {
    p = withPhotoOverride(p);
    setEditingPessoa(p);
    setPessoaForm({
      nome_completo: p.nome_completo, genero: p.genero, data_nascimento: p.data_nascimento,
      email: p.email || '', whatsapp: p.whatsapp || '', whatsapp_pai: p.whatsapp_pai || '', whatsapp_mae: p.whatsapp_mae || '', foto_url: p.foto_url || '', foto_thumb_url: p.foto_thumb_url || p.foto_url || '', foto_size: p.foto_size || null, foto_content_type: p.foto_content_type || '', foto_thumb_size: p.foto_thumb_size || null, foto_thumb_content_type: p.foto_thumb_content_type || '', foto_pos_x: p.foto_pos_x ?? 50, foto_pos_y: p.foto_pos_y ?? 50, foto_zoom: p.foto_zoom ?? 1, batizado: !!p.batizado, selado: !!p.selado,
      endereco: p.endereco || p.endereco_formatado || '', endereco_formatado: p.endereco_formatado || p.endereco || '', logradouro: p.logradouro || '', numero: p.numero || '', bairro: p.bairro || '', cidade: p.cidade || '', uf: p.uf || '', cep: p.cep || '', complemento: p.complemento || '', lat: p.lat ?? null, lng: p.lng ?? null, google_place_id: p.google_place_id || '',
      nome_pai: p.nome_pai || '', nome_mae: p.nome_mae || '',
      auxiliar_jovens: !!p.auxiliar_jovens, grupo_visita: !!p.grupo_visita,
      organista: !!p.organista, instrutora_musicalizacao: !!p.instrutora_musicalizacao,
      integrante_ebi: !!p.integrante_ebi, musico: !!p.musico,
      instrumentos: p.instrumentos || [], instrutor_gem: !!p.instrutor_gem,
      aluno_gem: !!p.aluno_gem
    });
    setPessoaErrors({});
    setPessoaEditTab('info');
    setPessoaViewMode('edit');
    fetchAnotacoes(p.pessoa_id);
    setPessoaDialog(true);
  };

  const validatePessoa = () => {
    const e = {};
    if (!pessoaForm.nome_completo.trim()) e.nome_completo = 'Obrigatório';
    if (!pessoaForm.genero) e.genero = 'Obrigatório';
    if (!pessoaForm.data_nascimento) e.data_nascimento = 'Obrigatório';
    if (!pessoaForm.nome_pai.trim()) e.nome_pai = 'Obrigatório';
    if (!pessoaForm.nome_mae.trim()) e.nome_mae = 'Obrigatório';
    if (pessoaForm.email && !EMAIL_REGEX.test(pessoaForm.email)) e.email = 'Email inválido';
    if (pessoaForm.whatsapp && pessoaForm.whatsapp.replace(/\D/g, '').length < 11) e.whatsapp = 'Número incompleto';
    setPessoaErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSavePessoa = async () => {
    if (!validatePessoa() || !editingPessoa) return;
    try {
      const payload = buildAuxPessoaPayload(pessoaForm);
      const r = await fetch(`${API}/auxiliar/pessoas/${editingPessoa.pessoa_id}`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, credentials: 'include', body: JSON.stringify(payload) });
      if (!r.ok) {
        let msg = 'Erro ao atualizar';
        try { const e = await r.json(); msg = e.detail || msg; } catch (err) { console.warn('Falha ao ler erro da atualização', err); }
        throw new Error(msg);
      }
      toast.success('Pessoa atualizada!');
      setPessoaDialog(false);
      setEditingPessoa(null);
      initDashboard();
    } catch (err) { toast.error(err.message); }
  };

  const buildAuxPessoaPayload = (source = pessoaForm) => ({
        nome_completo: source.nome_completo,
        genero: source.genero,
        data_nascimento: source.data_nascimento,
        email: source.email || null,
        whatsapp: source.whatsapp || null,
        whatsapp_pai: source.whatsapp_pai || null,
        whatsapp_mae: source.whatsapp_mae || null,
        foto_url: source.foto_url || '',
        foto_thumb_url: source.foto_url ? (source.foto_thumb_url || source.foto_url || '') : '',
        foto_size: source.foto_url ? (source.foto_size || null) : null,
        foto_content_type: source.foto_url ? (source.foto_content_type || null) : null,
        foto_thumb_size: source.foto_url ? (source.foto_thumb_size || null) : null,
        foto_thumb_content_type: source.foto_url ? (source.foto_thumb_content_type || null) : null,
        foto_pos_x: Number(source.foto_pos_x ?? 50),
        foto_pos_y: Number(source.foto_pos_y ?? 50),
        foto_zoom: Number(source.foto_zoom ?? 1),
        endereco: source.endereco || '', endereco_formatado: source.endereco_formatado || source.endereco || '', logradouro: source.logradouro || '', numero: source.numero || '', bairro: source.bairro || '', cidade: source.cidade || '', uf: source.uf || '', cep: source.cep || '', complemento: source.complemento || '', lat: source.lat, lng: source.lng, google_place_id: source.google_place_id || '',
        batizado: !!source.batizado,
        selado: !!source.selado,
        nome_pai: source.nome_pai,
        nome_mae: source.nome_mae,
        auxiliar_jovens: !!source.auxiliar_jovens,
        grupo_visita: !!source.grupo_visita,
        organista: source.genero === 'feminino' ? !!source.organista : false,
        instrutora_musicalizacao: source.genero === 'feminino' ? !!source.instrutora_musicalizacao : false,
        integrante_ebi: source.genero === 'feminino' ? !!source.integrante_ebi : false,
        musico: source.genero === 'masculino' ? !!source.musico : false,
        instrumentos: (source.genero === 'masculino' && source.musico) ? source.instrumentos : [],
        instrutor_gem: source.genero === 'masculino' ? !!source.instrutor_gem : false,
        aluno_gem: source.genero === 'masculino' ? !!source.aluno_gem : false,
      });

  const withPhotoOverride = useCallback((pessoa) => {
    if (!pessoa?.pessoa_id || !photoOverrides[pessoa.pessoa_id]) return pessoa;
    return { ...pessoa, ...photoOverrides[pessoa.pessoa_id] };
  }, [photoOverrides]);

  const toggleInstrumento = (inst) => setPessoaForm(p => ({
    ...p, instrumentos: p.instrumentos.includes(inst) ? p.instrumentos.filter(i => i !== inst) : [...p.instrumentos, inst]
  }));

  const handlePessoaPhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const allowed = ['image/jpeg', 'image/png', 'image/webp'];
    if (!allowed.includes(file.type)) { toast.error('Envie uma foto JPG, PNG ou WebP.'); e.target.value = ''; return; }
    if (file.size > 3 * 1024 * 1024) { toast.error('Foto muito grande. Limite máximo: 3MB.'); e.target.value = ''; return; }
    setPessoaPhotoUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const r = await fetch(`${API}/upload/photo`, { method: 'POST', credentials: 'include', body: formData });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(data.detail || 'Erro ao enviar foto');
      const nextForm = { ...pessoaForm, foto_url: data.url || '', foto_thumb_url: data.thumb_url || data.url || '', foto_size: data.size || null, foto_content_type: data.content_type || '', foto_thumb_size: data.thumb_size || null, foto_thumb_content_type: data.thumb_content_type || '', foto_pos_x: 50, foto_pos_y: 50, foto_zoom: 1 };
      setPessoaForm(nextForm);
      if (editingPessoa?.pessoa_id) {
        const save = await fetch(`${API}/auxiliar/pessoas/${editingPessoa.pessoa_id}`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, credentials: 'include', body: JSON.stringify(buildAuxPessoaPayload(nextForm)) });
        const saved = await save.json().catch(() => ({}));
        if (!save.ok) throw new Error(saved.detail || 'Erro ao salvar foto');
        setEditingPessoa(saved);
        setPhotoOverrides(prev => ({ ...prev, [editingPessoa.pessoa_id]: { foto_url: saved.foto_url || '', foto_thumb_url: saved.foto_thumb_url || saved.foto_url || '', foto_size: saved.foto_size || null, foto_content_type: saved.foto_content_type || '', foto_thumb_size: saved.foto_thumb_size || null, foto_thumb_content_type: saved.foto_thumb_content_type || '', foto_pos_x: saved.foto_pos_x ?? 50, foto_pos_y: saved.foto_pos_y ?? 50, foto_zoom: saved.foto_zoom ?? 1 } }));
        toast.success('Foto enviada e salva automaticamente.');
        initDashboard();
      } else toast.success('Foto enviada. Salve o cadastro para concluir.');
    } catch (err) { toast.error(err.message); }
    finally { setPessoaPhotoUploading(false); e.target.value = ''; }
  };

  const saveAuxPhotoFrame = async (nextForm = pessoaForm, message = 'Enquadramento salvo.', refresh = true) => {
    if (!editingPessoa?.pessoa_id) return;
    setPessoaPhotoUploading(true);
    try {
      const r = await fetch(`${API}/auxiliar/pessoas/${editingPessoa.pessoa_id}`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, credentials: 'include', body: JSON.stringify(buildAuxPessoaPayload(nextForm)) });
      const saved = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(saved.detail || 'Erro ao salvar foto');
      setEditingPessoa(saved);
      setPhotoOverrides(prev => ({ ...prev, [editingPessoa.pessoa_id]: { foto_url: saved.foto_url || '', foto_thumb_url: saved.foto_thumb_url || saved.foto_url || '', foto_size: saved.foto_size || null, foto_content_type: saved.foto_content_type || '', foto_thumb_size: saved.foto_thumb_size || null, foto_thumb_content_type: saved.foto_thumb_content_type || '', foto_pos_x: saved.foto_pos_x ?? 50, foto_pos_y: saved.foto_pos_y ?? 50, foto_zoom: saved.foto_zoom ?? 1 } }));
      if (message) toast.success(message);
      if (refresh) initDashboard();
    } catch (err) { toast.error(err.message); }
    finally { setPessoaPhotoUploading(false); }
  };

  const removeAuxPhoto = async () => {
    const nextForm = { ...pessoaForm, foto_url: '', foto_thumb_url: '', foto_size: null, foto_content_type: '', foto_thumb_size: null, foto_thumb_content_type: '', foto_pos_x: 50, foto_pos_y: 50, foto_zoom: 1 };
    setPessoaForm(nextForm);
    await saveAuxPhotoFrame(nextForm, 'Foto removida.');
  };

  const scheduleAuxPhotoFrameAutoSave = (nextForm) => {
    if (!editingPessoa?.pessoa_id) return;
    setPhotoOverrides(prev => ({ ...prev, [editingPessoa.pessoa_id]: { foto_url: nextForm.foto_url || '', foto_thumb_url: nextForm.foto_thumb_url || '', foto_size: nextForm.foto_size || null, foto_content_type: nextForm.foto_content_type || '', foto_thumb_size: nextForm.foto_thumb_size || null, foto_thumb_content_type: nextForm.foto_thumb_content_type || '', foto_pos_x: nextForm.foto_pos_x ?? 50, foto_pos_y: nextForm.foto_pos_y ?? 50, foto_zoom: nextForm.foto_zoom ?? 1 } }));
    if (photoFrameSaveTimerRef.current) clearTimeout(photoFrameSaveTimerRef.current);
    photoFrameSaveTimerRef.current = setTimeout(async () => {
      await saveAuxPhotoFrame(nextForm, '', false);
    }, 700);
  };

  const fetchAnotacoes = async (pessoaId) => {
    setAnotLoading(true);
    try {
      const r = await fetch(`${API}/pessoas/${pessoaId}/anotacoes`, { credentials: 'include' });
      if (r.ok) setAnotacoes(await r.json());
    } catch (err) { console.error(err); }
    finally { setAnotLoading(false); }
  };

  const openAnotacoes = (p) => {
    setEditingPessoa(p);
    setPessoaForm({
      nome_completo: p.nome_completo, genero: p.genero, data_nascimento: p.data_nascimento,
      email: p.email || '', whatsapp: p.whatsapp || '', whatsapp_pai: p.whatsapp_pai || '', whatsapp_mae: p.whatsapp_mae || '', foto_url: p.foto_url || '', foto_thumb_url: p.foto_thumb_url || p.foto_url || '', foto_size: p.foto_size || null, foto_content_type: p.foto_content_type || '', foto_thumb_size: p.foto_thumb_size || null, foto_thumb_content_type: p.foto_thumb_content_type || '', batizado: !!p.batizado, selado: !!p.selado,
      endereco: p.endereco || p.endereco_formatado || '', endereco_formatado: p.endereco_formatado || p.endereco || '', logradouro: p.logradouro || '', numero: p.numero || '', bairro: p.bairro || '', cidade: p.cidade || '', uf: p.uf || '', cep: p.cep || '', complemento: p.complemento || '', lat: p.lat ?? null, lng: p.lng ?? null, google_place_id: p.google_place_id || '',
      nome_pai: p.nome_pai || '', nome_mae: p.nome_mae || '',
      auxiliar_jovens: !!p.auxiliar_jovens, grupo_visita: !!p.grupo_visita,
      organista: !!p.organista, instrutora_musicalizacao: !!p.instrutora_musicalizacao,
      integrante_ebi: !!p.integrante_ebi, musico: !!p.musico,
      instrumentos: p.instrumentos || [], instrutor_gem: !!p.instrutor_gem,
      aluno_gem: !!p.aluno_gem
    });
    setPessoaErrors({});
    setPessoaEditTab('anotacoes');
    fetchAnotacoes(p.pessoa_id);
    setPessoaDialog(true);
    setTimeout(() => { setEditingAnot(null); setAnotForm({ descricao: '', data: '' }); setAnotDialog(true); }, 300);
  };

  const handleSaveAnot = async () => {
    if (!anotForm.descricao.trim()) { toast.error('Descrição é obrigatória'); return; }
    try {
      const pid = editingPessoa.pessoa_id;
      const url = editingAnot ? `${API}/pessoas/${pid}/anotacoes/${editingAnot.anotacao_id}` : `${API}/pessoas/${pid}/anotacoes`;
      const r = await fetch(url, { method: editingAnot ? 'PUT' : 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include', body: JSON.stringify({ titulo: anotForm.data ? fmtDate(anotForm.data) : 'Anotação', descricao: anotForm.descricao, data: anotForm.data || null }) });
      if (!r.ok) { let msg = 'Erro'; try { const e = await r.json(); msg = e.detail || msg; } catch (err) { console.warn('Falha ao ler erro da anotação', err); } throw new Error(msg); }
      toast.success(editingAnot ? 'Anotação atualizada!' : 'Anotação adicionada!');
      setAnotDialog(false);
      setEditingAnot(null);
      setAnotForm({ descricao: '', data: '' });
      fetchAnotacoes(pid);
    } catch (err) { toast.error(err.message); }
  };

  const handleDeleteAnot = async (anotacaoId) => {
    try {
      const pid = editingPessoa.pessoa_id;
      const r = await fetch(`${API}/pessoas/${pid}/anotacoes/${anotacaoId}`, { method: 'DELETE', credentials: 'include' });
      if (!r.ok) { let msg = 'Erro'; try { const e = await r.json(); msg = e.detail || msg; } catch (err) { console.warn('Falha ao ler erro da exclusão', err); } throw new Error(msg); }
      toast.success('Anotação excluída!');
      fetchAnotacoes(pid);
    } catch (err) { toast.error(err.message); }
  };

  const fetchMySchedules = async () => {
    setMySchedulesLoading(true);
    try {
      const r = await fetch(`${API}/service-schedules/my-schedules?_t=${Date.now()}`, { credentials: 'include', cache: 'no-store' });
      if (r.ok) setMySchedules(await r.json());
    } catch (err) { console.warn('Falha ao buscar minhas escalas', err); } finally { setMySchedulesLoading(false); }
  };

  // Recitative functions for Contagem modal (same filter as admin EventosTab)
  const recFunctions = useMemo(() => {
    return serviceFunctions.filter(f => f.active !== false && (f.tipo === 'recitativo' || (f.tipo === 'outros' && (f.nome || '').toLowerCase().includes('recitativo individual'))));
  }, [serviceFunctions]);

  // Check if Contagem button should show. Any auxiliar can register recitative counts.
  const showContagem = useMemo(() => {
    if (!selectedEvent) return false;
    const tipo = tiposEvento.find(t => t.tipo_evento_id === selectedEvent.tipo_evento_id);
    if (!tipo?.tem_recitativos) return false;
    const eventDate = new Date(selectedEvent.date);
    return eventDate <= new Date();
  }, [selectedEvent, tiposEvento]);

  // All recitative functions are available to any auxiliar in the counting modal.
  const contagemRecFunctions = useMemo(() => recFunctions, [recFunctions]);

  const openContagemDialog = async () => {
    // Load existing recitativos from the event
    try {
      const r = await fetch(`${API}/events/${selectedEvent.event_id}`, { credentials: 'include' });
      if (r.ok) {
        const ev = await r.json();
        const existing = ev.recitativos || {};
        const form = {};
        contagemRecFunctions.forEach(f => { form[f.function_id] = existing[f.function_id] ?? ''; });
        setContagemForm(form);
      } else {
        const form = {};
        contagemRecFunctions.forEach(f => { form[f.function_id] = ''; });
        setContagemForm(form);
      }
    } catch {
      const form = {};
      contagemRecFunctions.forEach(f => { form[f.function_id] = ''; });
      setContagemForm(form);
    }
    setContagemDialog(true);
  };

  const handleSaveContagem = async () => {
    setContagemSaving(true);
    try {
      const payload = {};
      contagemRecFunctions.forEach(f => {
        const val = contagemForm[f.function_id];
        payload[f.function_id] = val !== '' && val !== undefined ? parseInt(val) : null;
      });
      const r = await fetch(`${API}/events/${selectedEvent.event_id}/recitativos`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ recitativos: payload })
      });
      if (!r.ok) throw new Error('Erro ao salvar contagem');
      toast.success('Contagem salva com sucesso!');
      setContagemDialog(false);
    } catch (err) { toast.error(err.message); } finally { setContagemSaving(false); }
  };

  const openMySchedules = () => {
    setMySchedulesOpen(true);
    setExpandedEvent(null);
    fetchMySchedules();
  };

  const markPresenca = async (eventId, pessoaId, presente) => {
    try {
      const r = await fetch(`${API}/attendance`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ event_id: eventId, pessoa_id: pessoaId, presente }),
      });
      if (r.ok) {
        const now_sp = new Date().toLocaleString('pt-BR', { timeZone: 'America/Sao_Paulo', day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
        if (presente === null) {
          // Unmarking - find out what the previous state was
          const prev = mySchedules.find(s => s.event_id === eventId);
          const prevState = prev?.presenca;
          const action = prevState === true ? 'Desmarcou Presente' : prevState === false ? 'Desmarcou Ausente' : 'Status resetado para Aguardando';
          const entry = `${now_sp} - ${action}`;
          setMySchedules(prev => prev.map(s => {
            if (s.event_id !== eventId) return s;
            return { ...s, presenca: null, historico: [...(s.historico || []), entry] };
          }));
          toast.success('Status alterado para Aguardando.');
        } else if (!presente) {
          let repLog = [];
          let repHistEntries = [];
          try {
            const repR = await fetch(`${API}/service-schedules/${eventId}/replace-absent`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              credentials: 'include',
              body: JSON.stringify({ pessoa_id: pessoaId }),
            });
            if (repR.ok) {
              const repData = await repR.json();
              repLog = (repData.replaced || []).map(r => ({
                function_name: r.function_name,
                replacement_name: r.replacement_name || '',
                replacement_id: r.replacement,
              }));
              repHistEntries = (repData.replaced || []).map(r => {
                const leaderTag = r.was_leader ? ' (líder)' : '';
                if (r.replacement_name) {
                  return `${now_sp} - Substituido em ${r.function_name}${leaderTag} por ${r.replacement_name}`;
                } else {
                  return `${now_sp} - Sem substituto disponivel para ${r.function_name}${leaderTag}`;
                }
              });
            }
          } catch (err) { console.warn('Falha ao executar substituição automática', err); }
          const entry = `${now_sp} - Registrou Ausente`;
          const replacedFuncIds = new Set(repLog.map(r => r.function_name));
          setMySchedules(prev => prev.map(s => {
            if (s.event_id !== eventId) return s;
            const updatedFunctions = (s.my_functions || []).filter(f => !replacedFuncIds.has(f.function_name));
            return { ...s, presenca: false, my_functions: updatedFunctions, historico: [...(s.historico || []), entry, ...repHistEntries], replacement_log: repLog.length > 0 ? repLog : (s.replacement_log || []) };
          }));
          toast.success('Ausência registrada. Substituição automática realizada.');
        } else {
          const entry = `${now_sp} - Registrou Presente`;
          setMySchedules(prev => prev.map(s => {
            if (s.event_id !== eventId) return s;
            return { ...s, presenca: true, historico: [...(s.historico || []), entry] };
          }));
          toast.success('Presença confirmada!');
        }
      }
    } catch (err) { console.warn('Falha ao marcar presença na escala', err); }
  };

  const handleAbsentClick = (s) => {
    if (s.presenca === false) {
      // Already absent - unmark (back to aguardando)
      markPresenca(s.event_id, s.pessoa_id, null);
      return;
    }
    setAbsenceConfirm({
      eventId: s.event_id,
      pessoaId: s.pessoa_id,
      eventName: s.event_name,
      functions: s.my_functions || [],
      filas: [],
    });
  };

  const confirmAbsence = () => {
    if (absenceConfirm) {
      if (absenceConfirm.fromHeader) {
        // From header: use markPresenca + update local attendance state
        markPresenca(absenceConfirm.eventId, absenceConfirm.pessoaId, false);
        setAttendance(prev => ({ ...prev, [absenceConfirm.pessoaId]: false }));
      } else {
        markPresenca(absenceConfirm.eventId, absenceConfirm.pessoaId, false);
      }
      setAbsenceConfirm(null);
    }
  };

  // Simplified: show all pessoas sorted by name
  const { minhas, minhasFilaGroups, meninosGroups, meninasGroups } = useMemo(() => {
    if (!auxiliar) return { minhas: [], minhasFilaGroups: [], meninosGroups: [], meninasGroups: [] };

    const allSorted = [...pessoas].sort((a, b) => a.nome_completo.localeCompare(b.nome_completo));
    const meninos = allSorted.filter(p => p.genero === 'masculino');
    const meninas = allSorted.filter(p => p.genero === 'feminino');

    const meninosGroups = meninos.length > 0 ? [{ fila: { fila_id: '__meninos', nome: 'Meninos', cor: '#3B82F6' }, members: meninos }] : [];
    const meninasGroups = meninas.length > 0 ? [{ fila: { fila_id: '__meninas', nome: 'Meninas', cor: '#EC4899' }, members: meninas }] : [];

    // Group "Minhas Pessoas" by recitativo functions where this auxiliar is assigned
    const myFuncs = serviceFunctions.filter(f => f.tipo === 'recitativo' && f.active !== false && (f.assigned_people || []).includes(auxiliar.auxiliar_id || auxiliar.pessoa_id));
    let minhasFilaGroups;
    if (myFuncs.length > 0) {
      minhasFilaGroups = myFuncs.map(func => {
        const idadeMin = func.eligibility?.idade_min || 0;
        const idadeMax = func.eligibility?.idade_max || 99;
        const members = allSorted.filter(p => {
          if (p.pessoa_id === (auxiliar.auxiliar_id || auxiliar.pessoa_id)) return false;
          const age = calcAge(p.data_nascimento);
          if (age === null) return false;
          const genderMatch = !func.eligibility?.gender || func.eligibility.gender === 'todos' || (p.genero || '').toLowerCase() === func.eligibility.gender;
          return age >= idadeMin && age <= idadeMax && genderMatch;
        });
        return { fila: { fila_id: func.function_id, nome: func.nome, cor: func.eligibility?.gender === 'feminino' ? '#EC4899' : '#3B82F6' }, members };
      }).filter(g => g.members.length > 0);
    } else {
      minhasFilaGroups = [...meninosGroups, ...meninasGroups];
    }

    const minhasCount = myFuncs.length > 0 ? minhasFilaGroups.reduce((s, g) => s + g.members.length, 0) : allSorted.length;

    return { minhas: myFuncs.length > 0 ? minhasFilaGroups.flatMap(g => g.members) : allSorted, minhasFilaGroups, meninosGroups, meninasGroups };
  }, [auxiliar, pessoas, serviceFunctions]);

  const filterBySearch = (list) => {
    if (!searchTerm) return list;
    return list.filter(p => norm(p.nome_completo).includes(norm(searchTerm)));
  };

  const myFilaNames = [];

  // Stats
  const stats = useMemo(() => {
    const avgEngajamento = pessoasStats.length > 0
      ? Math.round(pessoasStats.reduce((s, p) => s + (p.frequencia_pct || 0), 0) / pessoasStats.length)
      : 0;
    const avgIdade = minhas.length > 0
      ? Math.round(minhas.reduce((s, p) => { const a = calcAge(p.data_nascimento); return s + (a || 0); }, 0) / minhas.length)
      : 0;
    const naoBatizados = minhas.filter(p => {
      const age = calcAge(p.data_nascimento);
      return age !== null && age >= 12 && !p.batizado;
    }).length;
    return { avgEngajamento, pessoasCount: minhas.length, avgIdade, naoBatizados };
  }, [pessoasStats, minhas]);

  if (initError) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4">
        <Card className="max-w-md w-full p-8 text-center bg-white shadow-lg border-0">
          <UserCheck className="w-12 h-12 text-red-400 mx-auto mb-4" />
          <h2 className="text-xl font-bold mb-2" style={{ fontFamily: 'Work Sans, sans-serif' }}>Acesso Negado</h2>
          <p className="text-slate-600 mb-6">{initError}</p>
          <Button onClick={() => navigate('/')} variant="outline">Voltar ao Login</Button>
        </Card>
      </div>
    );
  }

  if (!user || !auxiliar) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-slate-600">Carregando...</p>
        </div>
      </div>
    );
  }

  const pessoaIdSet = new Set(pessoas.map(p => p.pessoa_id));
  const presentCount = selectedEvent ? Object.entries(attendance).filter(([pid, v]) => v && pessoaIdSet.has(pid)).length : 0;

  return (
    <div className="min-h-screen bg-slate-50">
      <TestBanner />
      {/* Impersonate Banner */}
      {isImpersonating && (
        <div className="bg-amber-500 text-white text-center py-2 px-4 text-sm font-medium sticky top-0 z-[60] flex items-center justify-center gap-3" data-testid="impersonate-banner">
          <span>Visualizando como: <strong>{auxiliar?.name}</strong> — Sessão temporária (30 min)</span>
          <button onClick={async () => {
            try {
              const r = await fetch(`${API}/auth/end-impersonate`, { method: 'POST', credentials: 'include' });
              if (r.ok) {
                window.location.href = '/admin';
              } else {
                window.location.href = '/admin';
              }
            } catch { window.location.href = '/admin'; }
          }} className="px-3 py-1 bg-white/20 hover:bg-white/30 rounded text-xs font-semibold transition-colors" data-testid="end-impersonate-btn">
            Encerrar e voltar
          </button>
        </div>
      )}
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-orange-600 to-orange-500 rounded-xl flex items-center justify-center shadow-md flex-shrink-0">
                <UserCheck className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
              </div>
              <div>
                <h1 className="text-xl sm:text-2xl font-bold tracking-tight" style={{ fontFamily: 'Work Sans, sans-serif' }} data-testid="auxiliar-title">
                  {settings.app_name}
                </h1>
                <div className="flex items-center gap-1.5 flex-wrap mt-1">
                  {myFilaNames.map((fila) => (
                    <Badge key={fila.fila_id} variant="outline" className="text-xs hidden sm:inline-flex">
                      <Rows3 className="w-3 h-3 mr-1" />{fila.nome}
                    </Badge>
                  ))}
                  <Badge variant="outline" className="text-xs hidden sm:inline-flex">
                    {auxiliar.genero === 'masculino' ? <><Baby className="w-3 h-3 mr-1" />Meninos</> : <><Flower2 className="w-3 h-3 mr-1" />Meninas</>}
                  </Badge>
                  <Badge variant="outline" className="text-xs"><User className="w-3 h-3 mr-1" />{auxiliar.name}</Badge>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <AccessShortcutButton navigate={navigate} testId="auxiliar-access-shortcut-button" />
              <Button onClick={() => setActiveTab('presenca')} variant="outline" size="icon" data-testid="my-schedules-btn" title="Minhas Escalas"><ClipboardCheck className="w-4 h-4" /></Button>
              <Button onClick={() => { const s = settings?.slug_linktree || settings?.slug_cadastro; if (s) { navigator.clipboard.writeText(`${window.location.origin}/l/${s}`); toast.success('Link da página pública copiado!'); } }} variant="outline" size="icon" data-testid="copy-linktree-btn" title="Copiar Link Página Pública"><Link className="w-4 h-4" /></Button>
              <div className="relative" ref={bellRef}>
                <NotificationBellIcon notifications={notifications} onClick={() => setBellOpen(true)} />
                <NotificationModal open={bellOpen} onOpenChange={setBellOpen} notifications={notifications} onRefresh={fetchNotifications} />
              </div>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="sm" className="flex items-center gap-2" data-testid="auxiliar-menu-btn">
                    <Menu className="w-5 h-5" /><span className="hidden sm:inline">Menu</span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48">
                  <DropdownMenuItem onClick={() => setHelpOpen(true)} className="cursor-pointer" data-testid="menu-help">
                    <HelpCircle className="w-4 h-4 mr-2" />Ajuda
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleLogout} className="cursor-pointer text-red-600" data-testid="menu-logout">
                    <LogOut className="w-4 h-4 mr-2" />Sair
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </div>
        <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 border-t border-slate-100">
          <div className="flex gap-0.5 py-2 overflow-visible" data-testid="auxiliar-tabs">
            <button onClick={() => selectAuxTab('dashboard')} data-testid="tab-dashboard"
              className={`flex items-center gap-1.5 px-3 py-1.5 text-xs sm:text-sm font-medium whitespace-nowrap rounded-full transition-[background-color,color,box-shadow] duration-200 ${activeTab === 'dashboard' ? 'bg-orange-600 text-white shadow-sm' : 'text-slate-600 hover:bg-slate-100'}`}>
              <BarChart3 className="w-3.5 h-3.5" />Dashboard
            </button>
            <button onClick={() => selectAuxTab('pessoas')} data-testid="tab-pessoas"
              className={`flex items-center gap-1.5 px-3 py-1.5 text-xs sm:text-sm font-medium whitespace-nowrap rounded-full transition-[background-color,color,box-shadow] duration-200 ${activeTab === 'pessoas' ? 'bg-orange-600 text-white shadow-sm' : 'text-slate-600 hover:bg-slate-100'}`}>
              <UserRound className="w-3.5 h-3.5" />Pessoas
            </button>
            <div className="relative" ref={managementMenuRef} onMouseEnter={openManagementMenu} onMouseLeave={() => setManagementMenuPinnedClosed(false)}>
              <button type="button" data-testid="aux-tab-gestao-dropdown-trigger" aria-haspopup="menu" aria-expanded={managementMenuOpen}
                onClick={() => {
                  if (managementMenuOpen) {
                    setManagementMenuOpen(false);
                    setManagementMenuPinnedClosed(true);
                  } else {
                    setManagementMenuPinnedClosed(false);
                    setManagementMenuOpen(true);
                  }
                }}
                className={`flex items-center gap-1.5 px-3 py-1.5 text-xs sm:text-sm font-medium whitespace-nowrap rounded-full transition-[background-color,color,box-shadow] duration-200 ${['presenca','aniversariantes','votacoes','reunioes','cestas_basicas','batismos'].includes(activeTab) ? 'bg-orange-600 text-white shadow-sm' : 'text-slate-600 hover:bg-slate-100'}`}>
                <Settings className="w-3.5 h-3.5" />Gestão<ChevronDown className={`w-3.5 h-3.5 transition-transform duration-200 ${managementMenuOpen ? 'rotate-180' : ''}`} />
              </button>
              {managementMenuOpen && (
                <div role="menu" data-testid="aux-tab-gestao-dropdown-menu" className="absolute left-0 top-full z-[70] mt-2 w-56 rounded-xl border border-slate-200 bg-white p-2 text-slate-700 shadow-xl animate-in fade-in-0 zoom-in-95 duration-150">
                  {[
                    { key: 'presenca', label: 'Presença', icon: <CheckCircle2 className="w-3.5 h-3.5" /> },
                    ...(settings?.addons?.aniversariantes !== false ? [{ key: 'aniversariantes', label: 'Aniversariantes', icon: <Cake className="w-3.5 h-3.5" /> }] : []),
                    ...(settings?.addons?.votacoes ? [{ key: 'votacoes', label: 'Votações', icon: <Vote className="w-3.5 h-3.5" /> }] : []),
                    ...(settings?.addons?.reunioes ? [{ key: 'reunioes', label: 'Reuniões', icon: <BookOpen className="w-3.5 h-3.5" /> }] : []),
                    ...(settings?.addons?.cestas_basicas ? [{ key: 'cestas_basicas', label: 'Montagens', icon: <Package className="w-3.5 h-3.5" /> }] : []),
                    ...(settings?.addons?.batismos ? [{ key: 'batismos', label: 'Batismos', icon: <Droplets className="w-3.5 h-3.5" /> }] : []),
                  ].map(item => (
                    <button type="button" key={item.key} onClick={() => selectAuxTab(item.key)} data-testid={`aux-tab-gestao-${item.key}`}
                      className={`flex w-full cursor-pointer items-center gap-2 rounded-lg px-3 py-2 text-left text-sm transition-colors ${activeTab === item.key ? 'bg-orange-50 text-orange-700 font-semibold' : 'text-slate-700 hover:bg-slate-100'}`}>
                      {item.icon}<span>{item.label}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
            <button onClick={() => selectAuxTab('relatorios')} data-testid="tab-relatorios"
              className={`flex items-center gap-1.5 px-3 py-1.5 text-xs sm:text-sm font-medium whitespace-nowrap rounded-full transition-[background-color,color,box-shadow] duration-200 ${activeTab === 'relatorios' ? 'bg-orange-600 text-white shadow-sm' : 'text-slate-600 hover:bg-slate-100'}`}>
              <BarChart3 className="w-3.5 h-3.5" />Relatórios
            </button>
          </div>
        </nav>
      </header>
      {/* Notifications only via bell icon */}

      <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-4 sm:py-6">

        {activeTab === 'dashboard' && (
          <DashboardTab
            overview={{ total_pessoas: pessoas.length, total_auxiliares: 0, total_pendentes: 0 }}
            pessoas={pessoas}
            pessoasStats={pessoasStats}
            eventsEngagement={eventsEngagement}
            events={events}
            frequencyReport={frequencyReport}
            filas={[]}
            tiposEvento={tiposEvento}
          />
        )}

        {activeTab === 'presenca' && (
            <div className="grid grid-cols-1 lg:grid-cols-4 gap-4 sm:gap-6">
              {/* Events List */}
              <div className="lg:col-span-1">
                <Card className="bg-white border-0 shadow-sm">
                  <CardHeader className="pb-3 sm:pb-4 px-3 sm:px-6 cursor-pointer sm:cursor-default" onClick={() => setEventsSidebarOpen(prev => !prev)}>
                    <div className="flex items-center gap-2 sm:gap-3">
                      <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg sm:rounded-xl bg-indigo-100 flex items-center justify-center flex-shrink-0">
                      <CheckCircle2 className="w-4 h-4 sm:w-5 sm:h-5 text-indigo-600" />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center justify-between">
                          <h2 className="text-lg font-semibold text-slate-900" style={{ fontFamily: 'Work Sans, sans-serif' }}>Presença</h2>
                          <div className="flex items-center gap-2">
                            <Badge className="bg-indigo-100 text-indigo-700 text-sm font-bold">{events.filter(e => new Date(e.date) <= new Date(Date.now() + 7 * 24 * 60 * 60 * 1000)).length}</Badge>
                            <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform sm:hidden ${eventsSidebarOpen ? 'rotate-180' : ''}`} />
                          </div>
                        </div>
                        <p className="text-sm text-slate-500">Próximos 7 dias</p>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent className={`px-3 sm:px-6 ${eventsSidebarOpen ? '' : 'hidden sm:block'}`}>
                    <div className="space-y-2" data-testid="events-list">
                      {(() => {
                        const now = new Date();
                        const in7days = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000);
                        const filtered = events.filter(e => new Date(e.date) <= in7days).sort((a, b) => new Date(a.date) - new Date(b.date));
                        return filtered.length > 0 ? filtered.map((event) => {
                        const isSelected = selectedEvent?.event_id === event.event_id;
                        const today = new Date().toISOString().split('T')[0];
                        const eventDay = (event.date || '').split('T')[0].split(' ')[0];
                        const isToday = eventDay === today;
                        const isFuture = new Date(event.date) > new Date();
                        return (
                          <button key={event.event_id} onClick={() => { setSelectedEvent(event); setSearchTerm(''); }}
                            data-testid={`event-item-${event.event_id}`}
                            className={`w-full text-left p-2.5 sm:p-3 rounded-lg border-2 transition-all duration-200 flex items-center gap-2 sm:gap-3 ${isSelected ? 'border-indigo-600 bg-indigo-50 shadow-sm' : 'border-transparent bg-slate-50 hover:bg-slate-100'}`}>
                            <div className={`w-8 h-8 sm:w-9 sm:h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${isFuture ? 'bg-amber-100' : 'bg-indigo-100'}`}>
                              {isFuture ? (
                                <TooltipProvider delayDuration={0}><Tooltip><TooltipTrigger asChild><span><CalendarClock className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-amber-600" /></span></TooltipTrigger><TooltipContent><p className="text-xs">Evento futuro</p></TooltipContent></Tooltip></TooltipProvider>
                              ) : <Calendar className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-indigo-600" />}
                            </div>
                            <div className="flex-1">
                              <div className="flex justify-between items-start">
                                <div className="flex items-center gap-1.5 flex-wrap">
                                  <h3 className="font-semibold text-sm text-slate-900 truncate">{event.name}</h3>
                                </div>
                              </div>
                              <p className="text-xs text-slate-500 mt-1">{fmtDate(event.date)}</p>
                            </div>
                          </button>
                        );
                      }) : (
                        <div className="text-center py-8">
                          <Calendar className="w-10 h-10 text-slate-300 mx-auto mb-2" />
                          <p className="text-sm text-slate-500">Nenhum evento nos próximos 7 dias</p>
                        </div>
                      );
                      })()}
                    </div>
                    <Button size="sm" variant="outline" onClick={() => setEventDialog(true)} className="w-full mt-3 border-dashed" data-testid="create-event-btn">
                      <Plus className="w-4 h-4 mr-1" />Novo Evento
                    </Button>
                  </CardContent>
                </Card>
              </div>

              {/* Attendance */}
              <div className="lg:col-span-3 flex flex-col gap-4">
              {selectedEvent && (() => {
                const myPid = auxiliar?.auxiliar_id || auxiliar?.pessoa_id;
                const isPresent = myPid && attendance[myPid] === true;
                if (!isPresent) return null;
                const presentIds = Object.entries(attendance).filter(([,v]) => v).map(([k]) => k);
                const presentPessoas = pessoas.filter(p => presentIds.includes(p.pessoa_id));
                const organistas = presentPessoas.filter(p => p.organista).length;
                const musicos = presentPessoas.filter(p => p.musico).length;
                const violeiros = presentPessoas.filter(p => p.musico && p.instrumentos?.includes('Violao')).length;
                return (
                  <div className="hidden sm:grid grid-cols-2 sm:grid-cols-4 gap-3">
                    <Card className="bg-white border-0 shadow-sm">
                      <CardContent className="p-3 sm:p-4">
                        <p className="text-[10px] sm:text-xs text-slate-500 mb-1 sm:mb-2">Presentes</p>
                        <div className="flex items-center gap-2">
                          <Users className="w-5 h-5 text-green-600" />
                          <p className="text-xl sm:text-2xl font-bold text-green-600">{presentCount}</p>
                        </div>
                      </CardContent>
                    </Card>
                    <Card className="bg-white border-0 shadow-sm">
                      <CardContent className="p-3 sm:p-4">
                        <p className="text-[10px] sm:text-xs text-slate-500 mb-1 sm:mb-2">Organistas</p>
                        <div className="flex items-center gap-2">
                          <Music className="w-5 h-5 text-orange-500" />
                          <p className="text-xl sm:text-2xl font-bold text-orange-500">{organistas}</p>
                        </div>
                      </CardContent>
                    </Card>
                    <Card className="bg-white border-0 shadow-sm">
                      <CardContent className="p-3 sm:p-4">
                        <p className="text-[10px] sm:text-xs text-slate-500 mb-1 sm:mb-2">Músicos</p>
                        <div className="flex items-center gap-2">
                          <Music className="w-5 h-5 text-red-600" />
                          <p className="text-xl sm:text-2xl font-bold text-red-600">{musicos}</p>
                        </div>
                      </CardContent>
                    </Card>
                    <Card className="bg-white border-0 shadow-sm">
                      <CardContent className="p-3 sm:p-4">
                        <p className="text-[10px] sm:text-xs text-slate-500 mb-1 sm:mb-2">Violeiros</p>
                        <div className="flex items-center gap-2">
                          <Guitar className="w-5 h-5 text-blue-600" />
                          <p className="text-xl sm:text-2xl font-bold text-blue-600">{violeiros}</p>
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                );
              })()}
              <Card className="bg-white border-0 shadow-sm">
                  <CardContent className="p-3 sm:p-4">
                    <div className="flex items-start gap-2 sm:gap-3">
                      <div className={`w-8 h-8 sm:w-10 sm:h-10 rounded-lg sm:rounded-xl flex items-center justify-center flex-shrink-0 mt-0.5 ${selectedEvent && new Date(selectedEvent.date) > new Date() ? 'bg-amber-100' : 'bg-indigo-100'}`}>
                        {selectedEvent && new Date(selectedEvent.date) > new Date() ? <CalendarClock className="w-4 h-4 sm:w-5 sm:h-5 text-amber-600" /> : <Calendar className="w-4 h-4 sm:w-5 sm:h-5 text-indigo-600" />}
                      </div>
                      <div className="flex-1 min-w-0">
                        {/* Row 1: Name + type badge */}
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <h2 className="text-base sm:text-lg font-semibold text-slate-900" style={{ fontFamily: 'Work Sans, sans-serif' }} data-testid="attendance-title">
                            {selectedEvent ? selectedEvent.name : 'Selecione um Evento'}
                          </h2>
                          {(() => { const tipo = selectedEvent && tiposEvento.find(t => t.tipo_evento_id === selectedEvent.tipo_evento_id); return tipo ? <Badge variant="outline" className="text-[10px] sm:text-xs font-medium" data-testid="aux-badge-tipo-selected">{tipo.nome}</Badge> : null; })()}
                          {selectedEvent && new Date(selectedEvent.date) > new Date() && <Badge variant="outline" className="text-[10px] sm:text-xs font-medium" data-testid="aux-badge-evento-futuro">Evento Futuro</Badge>}
                          {selectedEvent?.status === 'fechado' && <Badge className="bg-orange-100 text-orange-700 text-[10px] sm:text-xs font-semibold">Fechado</Badge>}
                        </div>
                        {/* Row 2: Date */}
                        {selectedEvent && <p className="text-xs text-slate-500 mt-0.5">{fmtDate(selectedEvent.date)}</p>}
                        {/* Row 3: Function badges (below name on mobile) */}
                        {selectedEvent && auxiliar && (() => {
                          const mySchedule = mySchedules.find(s => s.event_id === selectedEvent.event_id);
                          const myFuncs = mySchedule?.my_functions || [];
                          const repLog = mySchedule?.replacement_log || [];
                          const repMap = {};
                          for (const r of repLog) repMap[r.function_name] = r.replacement_name || '';
                          const activeFuncs = myFuncs.filter(f => !repMap[f.function_name]);
                          const redirectedFuncs = repLog.map(r => ({ function_name: r.function_name, replacement_name: r.replacement_name || '' }));
                          if (activeFuncs.length === 0 && redirectedFuncs.length === 0) return null;
                          return (
                            <div className="flex items-center gap-1 flex-wrap mt-1.5" data-testid="aux-header-functions">
                              {activeFuncs.map((f, i) => (
                                <span key={`active-${i}`} className="relative inline-flex">
                                  <Badge className="bg-blue-50 text-blue-700 border border-blue-200 text-[9px]">{f.function_name}</Badge>
                                  {f.is_leader && (
                                    <TooltipProvider delayDuration={0}><Tooltip><TooltipTrigger asChild>
                                      <span className="absolute -top-1 -right-1 w-3 h-3 rounded-full bg-amber-400 flex items-center justify-center ring-1 ring-white"><Star className="w-1.5 h-1.5 text-white fill-white" /></span>
                                    </TooltipTrigger><TooltipContent className="max-w-xs"><p className="text-xs">Você é o responsável por liderar a fila e escrever os recitativos neste evento.</p></TooltipContent></Tooltip></TooltipProvider>
                                  )}
                                </span>
                              ))}
                              {redirectedFuncs.map((r, i) => (
                                <TooltipProvider key={`redir-${i}`} delayDuration={0}>
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <Badge className="bg-slate-100 text-slate-400 border border-slate-200 text-[9px] line-through">{r.function_name}</Badge>
                                    </TooltipTrigger>
                                    <TooltipContent><p className="text-xs">Função redirecionada{r.replacement_name ? ` para ${r.replacement_name}` : ''} por ausência</p></TooltipContent>
                                  </Tooltip>
                                </TooltipProvider>
                              ))}
                            </div>
                          );
                        })()}
                      </div>
                    </div>
                    {/* Botões presença + fechar */}
                    {selectedEvent && auxiliar && (() => {
                      const myPid = auxiliar.auxiliar_id || auxiliar.pessoa_id;
                      const isPresent = attendance[myPid];
                      const handleHeaderPresence = async (presente) => {
                        await markPresenca(selectedEvent.event_id, myPid, presente);
                        setAttendance(prev => ({ ...prev, [myPid]: presente }));
                      };
                      const handleHeaderAbsent = () => {
                        if (isPresent === false) {
                          // Already absent - unmark
                          handleHeaderPresence(null);
                          return;
                        }
                        const mySchedule = mySchedules.find(s => s.event_id === selectedEvent.event_id);
                        setAbsenceConfirm({
                          eventId: selectedEvent.event_id,
                          pessoaId: myPid,
                          eventName: selectedEvent.name,
                          functions: mySchedule?.my_functions || [],
                          filas: [],
                          fromHeader: true,
                        });
                      };
                      return (
                        <div className="flex flex-wrap items-center gap-2 mt-3 pt-3 border-t border-slate-100">
                          <div className="flex items-center gap-1 bg-slate-100 rounded-lg p-0.5 flex-shrink-0">
                            <button
                              onClick={handleHeaderAbsent}
                              data-testid="aux-header-mark-absent"
                              className={`flex items-center gap-1 px-2.5 h-8 rounded-md text-xs font-semibold transition-colors ${isPresent === false ? 'bg-red-600 text-white shadow-sm' : 'text-slate-500 hover:bg-slate-200'}`}
                            >
                              <Circle className="w-3 h-3" />Ausente
                            </button>
                            <button
                              onClick={() => handleHeaderPresence(isPresent === true ? null : true)}
                              data-testid="aux-header-mark-present"
                              className={`flex items-center gap-1 px-2.5 h-8 rounded-md text-xs font-semibold transition-colors ${isPresent === true ? 'bg-green-600 text-white shadow-sm' : 'text-slate-500 hover:bg-slate-200'}`}
                            >
                              <CheckCircle2 className="w-3 h-3" />Presente
                            </button>
                          </div>
                          <div className="flex items-center gap-1.5 ml-auto">
                            {(() => {
                              const mySchedule = mySchedules.find(s => s.event_id === selectedEvent.event_id);
                              return mySchedule ? (
                                <TooltipProvider delayDuration={0}><Tooltip><TooltipTrigger asChild>
                                  <button onClick={() => setLogModal(mySchedule)} className="flex items-center justify-center w-8 h-8 rounded-md border border-slate-200 hover:bg-slate-100 transition-colors" data-testid="aux-header-history-btn">
                                    <BookOpen className="w-3.5 h-3.5 text-slate-500" />
                                  </button>
                                </TooltipTrigger><TooltipContent><p className="text-xs">Histórico</p></TooltipContent></Tooltip></TooltipProvider>
                              ) : null;
                            })()}
                            <TooltipProvider delayDuration={0}><Tooltip><TooltipTrigger asChild>
                              <button onClick={() => setEscalasViewOpen(true)} className="flex items-center justify-center w-8 h-8 rounded-md border border-slate-200 hover:bg-slate-100 transition-colors" data-testid="aux-header-escalas-btn">
                                <ClipboardCheck className="w-3.5 h-3.5 text-slate-500" />
                              </button>
                            </TooltipTrigger><TooltipContent><p className="text-xs">Escala</p></TooltipContent></Tooltip></TooltipProvider>
                            {showContagem && selectedEvent.status !== 'fechado' && (
                              <Button size="sm" variant="outline" onClick={openContagemDialog} className="text-slate-700 border-slate-300 hover:bg-slate-100 text-xs h-8" data-testid="aux-contagem-btn">
                                <Hash className="w-3.5 h-3.5 sm:mr-1" /><span className="hidden sm:inline">Contagem</span>
                              </Button>
                            )}
                            {isPresent === true && (() => {
                              if (selectedEvent.status === 'fechado') {
                                return (
                                  <Button size="sm" variant="outline" onClick={() => setReopenDialog(true)} className="text-green-600 border-green-300 hover:bg-green-50 text-xs h-8" data-testid="aux-reopen-event-btn">
                                    <Lock className="w-3.5 h-3.5 sm:mr-1" /><span className="hidden sm:inline">Reabrir</span>
                                  </Button>
                                );
                              }
                              const { canClose, label } = getCloseDeadlineInfo();
                              return canClose ? (
                                <Button size="sm" onClick={openCloseDialog} className="bg-orange-600 hover:bg-orange-700 text-white text-xs h-8" data-testid="aux-close-event-btn">
                                  <Lock className="w-3.5 h-3.5 sm:mr-1" /><span className="hidden sm:inline">Fechar</span>
                                </Button>
                              ) : (
                                <TooltipProvider delayDuration={0}><Tooltip><TooltipTrigger asChild><span>
                                  <Button size="sm" disabled className="bg-slate-300 text-slate-500 cursor-not-allowed text-xs h-8" data-testid="aux-close-event-btn-disabled">
                                    <Lock className="w-3.5 h-3.5 sm:mr-1" /><span className="hidden sm:inline">Fechar</span>
                                  </Button>
                                </span></TooltipTrigger><TooltipContent><p className="text-xs">{label}</p></TooltipContent></Tooltip></TooltipProvider>
                              );
                            })()}
                          </div>
                        </div>
                      );
                    })()}
                  </CardContent>
              </Card>
              {(() => {
                const myPid = auxiliar?.auxiliar_id || auxiliar?.pessoa_id;
                const isPresent = myPid && attendance[myPid] === true;
                if (!isPresent || !selectedEvent) return null;
                return (
              <Card className="bg-white border-0 shadow-sm">
                  <CardHeader className="pb-3 sm:pb-4 px-3 sm:px-6">
                      <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                        <div className="relative flex-1">
                          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 w-4 h-4" />
                          <Input ref={searchAttRef} value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="pl-10 bg-slate-50 border-slate-200" data-testid="search-pessoa" />
                        </div>
                        <div className="flex bg-slate-100 rounded-lg p-0.5 flex-shrink-0 w-full sm:w-auto">
                          <button onClick={() => setFilterMode('minhas')} data-testid="filter-minhas"
                            className={`flex-1 sm:flex-none px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${filterMode === 'minhas' ? 'bg-white shadow-sm text-blue-700' : 'text-slate-600 hover:text-slate-900'}`}>
                            Minhas
                          </button>
                          <button onClick={() => setFilterMode('meninos')} data-testid="filter-meninos"
                            className={`flex-1 sm:flex-none px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${filterMode === 'meninos' ? 'bg-white shadow-sm text-blue-700' : 'text-slate-600 hover:text-slate-900'}`}>
                            Meninos
                          </button>
                          <button onClick={() => setFilterMode('meninas')} data-testid="filter-meninas"
                            className={`flex-1 sm:flex-none px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${filterMode === 'meninas' ? 'bg-white shadow-sm text-blue-700' : 'text-slate-600 hover:text-slate-900'}`}>
                            Meninas
                          </button>
                          <button onClick={() => setQuickMode(!quickMode)} data-testid="aux-quick-mode-toggle"
                            className={`flex-1 sm:flex-none px-3 py-1.5 rounded-md text-xs font-medium transition-colors flex items-center gap-1 ${quickMode ? 'bg-amber-50 shadow-sm text-amber-700' : 'text-slate-600 hover:text-slate-900'}`}>
                            <Zap className="w-3 h-3" />Rápida
                          </button>
                        </div>
                      </div>
                  </CardHeader>
                  <CardContent>
                    {loading ? (
                      <div className="text-center py-8">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                        <p className="mt-2 text-sm text-slate-600">Carregando...</p>
                      </div>
                    ) : (
                      <div>
                        {filterMode === 'minhas' ? (
                          <>
                            {minhasFilaGroups.map(({ fila, members }) => {
                              const filtered = filterBySearch(members);
                              if (filtered.length === 0) return null;
                              return (
                                <div key={fila.fila_id} className="mb-4" data-testid={`fila-group-${fila.fila_id}`}>
                                  <div className="flex items-center gap-2 mb-2 px-2">
                                    <div className="w-3 h-3 rounded-full" style={{ backgroundColor: fila.cor }}></div>
                                    <span className="text-sm font-semibold text-slate-700">{fila.nome}</span>
                                    <Badge variant="outline" className="text-xs">{filtered.length}</Badge>
                                  </div>
                                  {filtered.map((pessoa) => (
                                    <AttendanceRow key={pessoa.pessoa_id} pessoa={withPhotoOverride(pessoa)} presente={attendance[pessoa.pessoa_id]} onToggle={handleToggleAttendance} absentLast5={absentLast5} />
                                  ))}
                                </div>
                              );
                            })}
                            {filterBySearch(minhas).length === 0 && (
                              <p className="text-center text-slate-500 py-6">Nenhuma pessoa encontrada</p>
                            )}
                          </>
                        ) : (
                          <>
                            {(filterMode === 'meninos' ? meninosGroups : meninasGroups).map(({ fila, members }) => {
                              const filtered = filterBySearch(members);
                              if (filtered.length === 0) return null;
                              return (
                                <div key={fila.fila_id} className="mb-4" data-testid={`${filterMode}-fila-group-${fila.fila_id}`}>
                                  <div className="flex items-center gap-2 mb-2 px-2">
                                    <div className="w-3 h-3 rounded-full" style={{ backgroundColor: fila.cor }}></div>
                                    <span className="text-sm font-semibold text-slate-700">{fila.nome}</span>
                                    <Badge variant="outline" className="text-xs">{filtered.length}</Badge>
                                  </div>
                                  {filtered.map((pessoa) => (
                                    <AttendanceRow key={pessoa.pessoa_id} pessoa={withPhotoOverride(pessoa)} presente={attendance[pessoa.pessoa_id]} onToggle={handleToggleAttendance} absentLast5={absentLast5} />
                                  ))}
                                </div>
                              );
                            })}
                            {(filterMode === 'meninos' ? meninosGroups : meninasGroups).every(({ members }) => filterBySearch(members).length === 0) && (
                              <p className="text-center text-slate-500 py-6">Nenhuma pessoa encontrada</p>
                            )}
                          </>
                        )}
                      </div>
                    )}
                  </CardContent>
                </Card>
                );
              })()}
              </div>
            </div>
        )}

        {activeTab === 'pessoas' && (
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
            {/* Sidebar */}
            <div className="lg:col-span-1">
              <Card className="bg-white border-0 shadow-sm">
                <CardHeader className="pb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center flex-shrink-0">
                      <UserRound className="w-5 h-5 text-blue-600" />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center justify-between">
                        <h2 className="text-lg font-semibold text-slate-900" style={{ fontFamily: 'Work Sans, sans-serif' }}>Pessoas</h2>
                        <Badge className="bg-blue-100 text-blue-700 text-sm font-bold">{pessoas.length}</Badge>
                      </div>
                      <p className="text-sm text-slate-500">Filtre por grupo</p>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2" data-testid="aux-pessoas-menu">
                    {[
                      { key: 'minhas', label: 'Minhas Pessoas', icon: 'bg-orange-100', iconColor: 'text-orange-600', count: minhas.length },
                      { key: 'meninos', label: 'Meninos', icon: 'bg-blue-100', iconColor: 'text-blue-600', count: meninosGroups.reduce((s, g) => s + g.members.length, 0) },
                      { key: 'meninas', label: 'Meninas', icon: 'bg-pink-100', iconColor: 'text-pink-600', count: meninasGroups.reduce((s, g) => s + g.members.length, 0) },
                      { key: 'incompletos', label: 'Incompletos', icon: 'bg-purple-100', iconColor: 'text-purple-600', count: pessoas.filter(p => !p.data_nascimento).length },
                    ].map(item => (
                      <button key={item.key} onClick={() => setPessoaFilterMode(item.key)} data-testid={`pessoas-filter-${item.key}`}
                        className={`w-full text-left p-3 rounded-lg border-2 transition-all duration-200 flex items-center justify-between gap-3 ${pessoaFilterMode === item.key ? 'border-blue-600 bg-blue-50 shadow-sm' : 'border-transparent bg-slate-50 hover:bg-slate-100'}`}>
                        <div className="flex items-center gap-3">
                          <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${item.icon}`}>
                            {item.key === 'incompletos' ? <AlertTriangle className={`w-4 h-4 ${item.iconColor}`} /> : <ListFilter className={`w-4 h-4 ${item.iconColor}`} />}
                          </div>
                          <p className={`font-semibold text-sm ${pessoaFilterMode === item.key ? 'text-blue-700' : 'text-slate-900'}`}>{item.label}</p>
                        </div>
                        <Badge className={`text-xs font-semibold ${pessoaFilterMode === item.key ? 'bg-blue-100 text-blue-700' : 'bg-slate-100 text-slate-500'}`}>{item.count}</Badge>
                      </button>
                    ))}
                  </div>
                  <div className="mt-4">
                    <Button variant="outline" size="sm" onClick={downloadContactsVcf} className="w-full justify-center text-slate-600 hover:text-blue-600" data-testid="aux-download-contacts-vcf-btn">
                      <Download className="w-4 h-4 mr-1" />Download VCF/vCard
                    </Button>
                    <Button variant="outline" size="sm" onClick={openBirthdayCalendarDialog} className="mt-2 w-full justify-center text-slate-600 hover:text-blue-600" data-testid="aux-birthday-calendar-link-btn">
                      <Cake className="w-4 h-4 mr-1" />Agenda de Aniversários
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>
            {/* Content */}
            <div className="lg:col-span-3">
              <Card className="bg-white border-0 shadow-sm">
                <CardHeader className="pb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-lg bg-blue-100 flex items-center justify-center flex-shrink-0">
                      <UserRound className="w-5 h-5 text-blue-600" />
                    </div>
                    <div>
                      <h2 className="text-lg font-semibold text-slate-900" style={{ fontFamily: 'Work Sans, sans-serif' }}>
                        {pessoaFilterMode === 'minhas' ? 'Minhas Pessoas' : pessoaFilterMode === 'meninos' ? 'Meninos' : pessoaFilterMode === 'meninas' ? 'Meninas' : 'Incompletos'}
                      </h2>
                      <p className="text-sm text-slate-500">
                        {pessoaFilterMode === 'incompletos' ? `${pessoas.filter(p => !p.data_nascimento).length} pessoas` : (() => { const groups = pessoaFilterMode === 'minhas' ? minhasFilaGroups : pessoaFilterMode === 'meninos' ? meninosGroups : meninasGroups; return groups.reduce((s, g) => s + g.members.length, 0); })()} {pessoaFilterMode !== 'incompletos' ? 'pessoas' : ''}
                      </p>
                    </div>
                  </div>
                  <div className="relative mt-3">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 w-4 h-4" />
                    <Input value={pessoaSearch} onChange={(e) => setPessoaSearch(e.target.value)} className="pl-10 bg-slate-50 border-slate-200" placeholder="Buscar pessoa..." data-testid="pessoas-search" />
                  </div>
                </CardHeader>
                <CardContent>
                  {pessoaFilterMode === 'incompletos' ? (
                    <div>
                      {(() => {
                        const inc = pessoas.filter(p => !p.data_nascimento);
                        const filtered = pessoaSearch ? inc.filter(p => norm(p.nome_completo).includes(norm(pessoaSearch))) : inc;
                        return filtered.length > 0 ? filtered.map(person => { const p = withPhotoOverride(person); return (
                          <div key={p.pessoa_id} className="px-3 py-3 border-b last:border-b-0 border-slate-100" data-testid={`incompleto-${p.pessoa_id}`}>
                            <div className="flex items-center gap-3">
                              <PessoaAvatar pessoa={p} className="w-8 h-8" fallbackClassName={p.genero === 'masculino' ? 'bg-gradient-to-br from-blue-400 to-blue-500' : 'bg-gradient-to-br from-pink-400 to-pink-500'} testId={`aux-incompleto-pessoa-photo-${p.pessoa_id}`} />
                              <div className="flex-1 min-w-0">
                                <p className="font-semibold text-sm text-slate-900 truncate">{p.nome_completo}</p>
                                <div className="flex items-center gap-2 text-xs text-slate-400 mt-0.5">
                                  {p.whatsapp && <span>{p.whatsapp}</span>}
                                  <Badge className="bg-purple-50 text-purple-600 text-[9px]">Precisa revisar todos os dados</Badge>
                                </div>
                              </div>
                            </div>
                          </div>
                        ); }) : (
                          <div className="text-center py-12">
                            <AlertTriangle className="w-10 h-10 text-slate-300 mx-auto mb-3" />
                            <p className="text-slate-500 text-sm">Nenhuma pessoa com cadastro incompleto</p>
                          </div>
                        );
                      })()}
                    </div>
                  ) : (
                  (() => {
                    const groups = pessoaFilterMode === 'minhas' ? minhasFilaGroups : pessoaFilterMode === 'meninos' ? meninosGroups : meninasGroups;
                    const searchFn = (list) => !pessoaSearch ? list : list.filter(p => norm(p.nome_completo).includes(norm(pessoaSearch)));
                    const hasResults = groups.some(({ members }) => searchFn(members).length > 0);
                    return (
                      <div>
                        {groups.map(({ fila, members }) => {
                          const filtered = searchFn(members);
                          if (filtered.length === 0) return null;
                          return (
                            <div key={fila.fila_id} className="mb-4" data-testid={`pessoas-fila-group-${fila.fila_id}`}>
                              <div className="flex items-center gap-2 mb-2 px-2">
                                <div className="w-3 h-3 rounded-full" style={{ backgroundColor: fila.cor }}></div>
                                <span className="text-sm font-semibold text-slate-700">{fila.nome}</span>
                                <Badge variant="outline" className="text-xs">{filtered.length}</Badge>
                              </div>
                              {filtered.map((pessoa) => (
                                <PessoaRow key={pessoa.pessoa_id} pessoa={withPhotoOverride(pessoa)} onEdit={openEditPessoa} onAnotacoes={openAnotacoes} />
                              ))}
                            </div>
                          );
                        })}
                        {!hasResults && (
                          <div className="text-center py-12">
                            <UserRound className="w-10 h-10 text-slate-300 mx-auto mb-3" />
                            <p className="text-slate-500 text-sm">{pessoaSearch ? 'Nenhum resultado encontrado' : 'Nenhuma pessoa nesta lista'}</p>
                          </div>
                        )}
                      </div>
                    );
                  })()
                  )}
                </CardContent>
              </Card>
            </div>
          </div>
        )}

        {settings?.addons?.votacoes && activeTab === 'votacoes' && <AuxiliarVotacoesTab />}
        {settings?.addons?.reunioes && activeTab === 'reunioes' && <AuxiliarReunioesTab votacoesEnabled={!!settings?.addons?.votacoes} />}
        {settings?.addons?.cestas_basicas && activeTab === 'cestas_basicas' && <CestasBasicasTab />}
        {settings?.addons?.batismos && activeTab === 'batismos' && <BatismosTab />}

        {activeTab === 'relatorios' && (
            <RelatoriosTab frequencyReport={frequencyReport} auxiliarReport={auxiliarReport} eventsEngagement={eventsEngagement} pessoas={pessoasStats} tiposEvento={tiposEvento} onEditPessoa={(pessoaId) => { const p = pessoas.find(x => x.pessoa_id === pessoaId); if (p) { openEditPessoa(p); } }} engajamentoDisplay={appSettings?.engajamento_display || 'niveis'} isAuxiliar />
        )}

        {settings?.addons?.aniversariantes !== false && activeTab === 'aniversariantes' && (
            <AniversariantesTab pessoas={pessoas} />
        )}
      </div>

      {/* Create Event Dialog */}
      <Dialog open={eventDialog} onOpenChange={setEventDialog}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Novo Evento</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {/* Tipo de Evento - PRIMEIRO */}
            {tiposEvento.length > 0 && (
              <div>
                <Label className="text-sm font-semibold text-slate-800 mb-2 block">Tipo de Evento *</Label>
                <div className={`grid gap-2 ${tiposEvento.length <= 3 ? `grid-cols-${tiposEvento.length}` : 'grid-cols-3'}`}>
                  {tiposEvento.map(t => {
                    const active = eventForm.tipo_evento_id === t.tipo_evento_id;
                    return (
                      <button key={t.tipo_evento_id} type="button" onClick={() => setEventForm({ ...eventForm, tipo_evento_id: t.tipo_evento_id })}
                        className={`py-1.5 px-2 rounded-lg border-2 transition-all flex items-center justify-center gap-1.5 border-slate-200 bg-white hover:border-slate-300`}
                        style={active ? { backgroundColor: '#f0f6ff', borderColor: '#2662eb' } : {}}
                        data-testid={`aux-tipo-evento-${t.tipo_evento_id}`}>
                        <Calendar className={`w-3.5 h-3.5 ${active ? 'text-[#2662eb]' : 'text-slate-400'}`} />
                        <span className={`text-[11px] font-semibold leading-tight ${active ? 'text-[#2662eb]' : 'text-slate-700'}`}>{t.nome}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {selectedTipoEvento?.permite_pessoa && (
              <div className="relative mt-2" data-testid="aux-pessoa-associada-section">
                <div className="flex items-center gap-1.5 mb-2">
                  <Label className="text-sm font-semibold text-slate-800">Quem irá receber o evento</Label>
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild><Info className="w-3.5 h-3.5 text-slate-400" /></TooltipTrigger>
                      <TooltipContent className="max-w-xs"><p className="text-xs">Este campo não é obrigatório. Ele aparece porque o Tipo de Evento selecionado foi configurado para permitir a associação de uma pessoa.</p></TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </div>
                {eventForm.pessoa_id ? (
                  <div className="flex items-center gap-2 p-2.5 border border-blue-200 bg-blue-50 rounded-lg">
                    <PessoaAvatar pessoa={{ nome_completo: eventForm.pessoa_nome, foto_url: eventForm.pessoa_foto_url, foto_thumb_url: eventForm.pessoa_foto_url }} className="w-8 h-8" fallbackClassName="bg-blue-600" testId="aux-selected-event-pessoa-photo" />
                    <span className="text-sm font-medium text-slate-900 flex-1 truncate">{eventForm.pessoa_nome}</span>
                    <button type="button" className="text-slate-400 hover:text-red-500" onClick={() => { setEventForm({ ...eventForm, pessoa_id: '', pessoa_nome: '', pessoa_foto_url: '' }); setAuxPessoaSearch(''); }} data-testid="aux-remove-pessoa-associada"><X className="w-4 h-4" /></button>
                  </div>
                ) : (
                  <div className="relative">
                    <Input
                      value={auxPessoaSearch}
                      onChange={(e) => { setAuxPessoaSearch(e.target.value); setAuxPessoaDropdown(true); }}
                      onFocus={() => setAuxPessoaDropdown(true)}
                      data-testid="aux-pessoa-search-input"
                    />
                    {auxPessoaDropdown && auxPessoaSearch.length >= 2 && (() => {
                      const results = pessoas.filter(p => norm(p.nome_completo).includes(norm(auxPessoaSearch))).slice(0, 8);
                      if (results.length === 0) return null;
                      return (
                        <div className="absolute z-50 w-full mt-1 bg-white border border-slate-200 rounded-lg shadow-lg max-h-48 overflow-y-auto" data-testid="aux-pessoa-search-results">
                          {results.map(p => (
                            <button key={p.pessoa_id} type="button" className="w-full text-left px-3 py-2 hover:bg-slate-50 flex items-center gap-2 text-sm border-b border-slate-50 last:border-b-0"
                              onClick={() => { setEventForm({ ...eventForm, pessoa_id: p.pessoa_id, pessoa_nome: p.nome_completo, pessoa_foto_url: p.foto_url || '' }); setAuxPessoaSearch(p.nome_completo); setAuxPessoaDropdown(false); }}
                              data-testid={`aux-pessoa-option-${p.pessoa_id}`}>
                              <PessoaAvatar pessoa={withPhotoOverride(p)} className="w-7 h-7" fallbackClassName="bg-indigo-100 text-indigo-700 text-[10px]" testId={`aux-pessoa-option-photo-${p.pessoa_id}`} />
                              <span className="truncate">{p.nome_completo}</span>
                            </button>
                          ))}
                        </div>
                      );
                    })()}
                  </div>
                )}
              </div>
            )}

            <div>
              <Label>Nome do evento *</Label>
              <Input value={eventForm.name} onChange={(e) => setEventForm({ ...eventForm, name: e.target.value })} data-testid="event-name-input" />
            </div>
            <div>
              <Label>Data e Hora *</Label>
              <Input type="datetime-local" value={eventForm.date} onChange={(e) => setEventForm({ ...eventForm, date: e.target.value })} data-testid="event-date-input" />
            </div>

            <Button onClick={handleCreateEvent} disabled={saving} className="w-full bg-blue-600 hover:bg-blue-700" data-testid="save-event-btn">{saving ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" />Aguarde...</> : 'Criar Evento'}</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Pessoa Dialog - Informações + Anotações */}
      <Dialog open={pessoaDialog} onOpenChange={(o) => { if (!o) { setPessoaDialog(false); setEditingPessoa(null); setPessoaErrors({}); setAnotacoes([]); } else setPessoaDialog(o); }}>
        <DialogContent className="max-w-3xl max-h-[85vh] p-0 overflow-hidden">
          <div className="flex h-[75vh]">
            <div className="w-12 sm:w-56 bg-slate-50 border-r border-slate-200 p-2 sm:p-4 flex flex-col gap-1 flex-shrink-0">
              <p className="hidden sm:block text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2 px-2">Editar Pessoa</p>
              <button onClick={() => setPessoaEditTab('info')} data-testid="aux-pessoa-tab-info" className={`flex items-center justify-center sm:justify-start gap-2 px-2 sm:px-3 py-2 rounded-lg text-sm font-medium transition-colors text-left ${pessoaEditTab === 'info' ? 'bg-blue-600 text-white' : 'text-slate-700 hover:bg-slate-200'}`}>
                <Edit className="w-4 h-4 flex-shrink-0" /><span className="hidden sm:inline">Informações</span>
              </button>
              <button onClick={() => setPessoaEditTab('anotacoes')} data-testid="aux-pessoa-tab-anotacoes" className={`flex items-center justify-center sm:justify-start gap-2 px-2 sm:px-3 py-2 rounded-lg text-sm font-medium transition-colors text-left relative ${pessoaEditTab === 'anotacoes' ? 'bg-blue-600 text-white' : 'text-slate-700 hover:bg-slate-200'}`}>
                <StickyNote className="w-4 h-4 flex-shrink-0" /><span className="hidden sm:inline">Anotações</span>
                {anotacoes.length > 0 && <span className="sm:ml-auto text-xs bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded-full absolute -top-1 -right-1 sm:static">{anotacoes.length}</span>}
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-4 sm:p-6 pt-8 sm:pt-10">
              {pessoaEditTab === 'info' && (
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold text-slate-900" style={{ fontFamily: 'Work Sans, sans-serif' }}>Informações</h3>
                  <div className="flex flex-col gap-3 rounded-xl border border-slate-200 bg-slate-50 p-3 sm:flex-row sm:items-start" data-testid="aux-pessoa-photo-section">
                    <PessoaAvatar pessoa={pessoaForm} className="h-28 w-28 self-center sm:h-36 sm:w-36 sm:self-start" fallbackClassName="bg-gradient-to-br from-orange-500 to-orange-600 text-4xl border-4 border-white" testId={pessoaForm.foto_url ? 'aux-pessoa-photo-preview' : 'aux-pessoa-photo-avatar'} preferThumb={false} />
                    <div className="min-w-0 flex-1">
                      <div className="mb-2 flex flex-wrap justify-end gap-2" data-testid="aux-pessoa-photo-actions">
                        {pessoaForm.foto_url && <Button type="button" size="icon" variant="ghost" onClick={removeAuxPhoto} className="h-8 w-8 text-red-600 hover:bg-red-50" data-testid="aux-pessoa-photo-remove-button" title="Excluir foto"><X className="w-4 h-4" /></Button>}
                        <Button type="button" size="icon" variant="outline" onClick={() => pessoaPhotoInputRef.current?.click()} disabled={pessoaPhotoUploading} data-testid="aux-pessoa-photo-upload-button" title={pessoaForm.foto_url ? 'Trocar foto' : 'Enviar foto'}>
                          {pessoaPhotoUploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                        </Button>
                      <input ref={pessoaPhotoInputRef} type="file" accept="image/jpeg,image/png,image/webp" onChange={handlePessoaPhotoUpload} className="hidden" data-testid="aux-pessoa-photo-file-input" />
                      </div>
                      {pessoaForm.foto_url && <div className="mb-2 inline-flex items-center rounded-full border border-slate-200 bg-white px-2 py-1 text-[11px] font-bold text-slate-600" data-testid="aux-pessoa-photo-size-badge">Otimizada: {formatPhotoSize(pessoaForm.foto_size)}</div>}
                      {pessoaForm.foto_url && <PhotoFrameControls form={pessoaForm} setForm={setPessoaForm} onAutoSave={scheduleAuxPhotoFrameAutoSave} prefix="aux-pessoa" />}
                    </div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="col-span-1 sm:col-span-2"><Label>Nome Completo *</Label><Input value={pessoaForm.nome_completo} onChange={(e) => setPessoaForm({ ...pessoaForm, nome_completo: e.target.value })} className={pessoaErrors.nome_completo ? 'border-red-500' : ''} data-testid="edit-pessoa-nome" />{pessoaErrors.nome_completo && <p className="text-xs text-red-500 mt-1">{pessoaErrors.nome_completo}</p>}</div>
                    <div>
                      <Label>Gênero *</Label>
                      <Select value={pessoaForm.genero} onValueChange={(v) => setPessoaForm(prev => ({ ...prev, genero: v, organista: false, instrutora_musicalizacao: false, integrante_ebi: false, musico: false, instrumentos: [], instrutor_gem: false, aluno_gem: false }))}>
                        <SelectTrigger className={pessoaErrors.genero ? 'border-red-500' : ''} data-testid="edit-pessoa-genero"><SelectValue /></SelectTrigger>
                        <SelectContent><SelectItem value="masculino">Masculino</SelectItem><SelectItem value="feminino">Feminino</SelectItem></SelectContent>
                      </Select>
                      {pessoaErrors.genero && <p className="text-xs text-red-500 mt-1">{pessoaErrors.genero}</p>}
                    </div>
                    <div><Label>Data Nascimento *</Label><Input type="date" value={pessoaForm.data_nascimento} onChange={(e) => setPessoaForm({ ...pessoaForm, data_nascimento: e.target.value })} className={pessoaErrors.data_nascimento ? 'border-red-500' : ''} data-testid="edit-pessoa-nascimento" />{pessoaErrors.data_nascimento && <p className="text-xs text-red-500 mt-1">{pessoaErrors.data_nascimento}</p>}</div>
                    <div><Label>Email</Label><Input type="email" value={pessoaForm.email} onChange={(e) => setPessoaForm({ ...pessoaForm, email: e.target.value })} className={pessoaErrors.email ? 'border-red-500' : ''} data-testid="edit-pessoa-email" />{pessoaErrors.email && <p className="text-xs text-red-500 mt-1">{pessoaErrors.email}</p>}</div>
                    <div><Label>WhatsApp</Label><Input value={pessoaForm.whatsapp} onChange={(e) => setPessoaForm({ ...pessoaForm, whatsapp: formatPhone(e.target.value) })} className={pessoaErrors.whatsapp ? 'border-red-500' : ''} data-testid="edit-pessoa-whatsapp" />{pessoaErrors.whatsapp && <p className="text-xs text-red-500 mt-1">{pessoaErrors.whatsapp}</p>}</div>
                    <div><Label>Nome do Pai *</Label><Input value={pessoaForm.nome_pai} onChange={(e) => setPessoaForm({ ...pessoaForm, nome_pai: e.target.value })} className={pessoaErrors.nome_pai ? 'border-red-500' : ''} data-testid="edit-pessoa-pai" />{pessoaErrors.nome_pai && <p className="text-xs text-red-500 mt-1">{pessoaErrors.nome_pai}</p>}</div>
                    <div><Label>WhatsApp Pai</Label><Input value={pessoaForm.whatsapp_pai} onChange={(e) => setPessoaForm({ ...pessoaForm, whatsapp_pai: formatPhone(e.target.value) })} data-testid="edit-pessoa-whatsapp-pai" /></div>
                    <div><Label>Nome da Mae *</Label><Input value={pessoaForm.nome_mae} onChange={(e) => setPessoaForm({ ...pessoaForm, nome_mae: e.target.value })} className={pessoaErrors.nome_mae ? 'border-red-500' : ''} data-testid="edit-pessoa-mae" />{pessoaErrors.nome_mae && <p className="text-xs text-red-500 mt-1">{pessoaErrors.nome_mae}</p>}</div>
                    <div><Label>WhatsApp Mae</Label><Input value={pessoaForm.whatsapp_mae} onChange={(e) => setPessoaForm({ ...pessoaForm, whatsapp_mae: formatPhone(e.target.value) })} data-testid="edit-pessoa-whatsapp-mae" /></div>
                    <div className="col-span-1 sm:col-span-2 grid grid-cols-1 gap-4" data-testid="aux-pessoa-address-section">
                      <div><Label>Endereço</Label><AddressAutocompleteInput value={pessoaForm.endereco_formatado || pessoaForm.endereco || ''} onChange={(value, place) => setPessoaForm(prev => ({ ...prev, ...(place ? parseGooglePlaceAddress(place, value) : { endereco: value, endereco_formatado: value, lat: null, lng: null, google_place_id: '' }) }))} placeholder="Buscar endereço..." testId="aux-pessoa-address-input" /></div>
                      <div><Label>Complemento</Label><Input value={pessoaForm.complemento || ''} onChange={(e) => setPessoaForm({ ...pessoaForm, complemento: e.target.value })} placeholder="Casa, bloco, apto..." data-testid="aux-pessoa-address-complement-input" /></div>
                    </div>
                  </div>
                  <div className="pt-4 flex flex-col gap-3">
                    <p className="text-sm font-semibold text-slate-700">Informações adicionais</p>
                    <div className="flex items-center space-x-2"><Checkbox checked={pessoaForm.batizado} onCheckedChange={(c) => setPessoaForm({ ...pessoaForm, batizado: !!c })} id="ax-bat" /><Label htmlFor="ax-bat" className="cursor-pointer">E batizado?</Label></div>
                    <div className="flex items-center space-x-2"><Checkbox checked={pessoaForm.selado} onCheckedChange={(c) => setPessoaForm({ ...pessoaForm, selado: !!c })} id="ax-sel" /><Label htmlFor="ax-sel" className="cursor-pointer">E selado?</Label></div>
                    <div className="flex items-center space-x-2"><Checkbox checked={pessoaForm.auxiliar_jovens} onCheckedChange={(c) => setPessoaForm({ ...pessoaForm, auxiliar_jovens: !!c })} id="ax-axj" /><Label htmlFor="ax-axj" className="cursor-pointer">E auxiliar de jovens?</Label></div>
                    <div className="flex items-center space-x-2"><Checkbox checked={pessoaForm.grupo_visita} onCheckedChange={(c) => setPessoaForm({ ...pessoaForm, grupo_visita: !!c })} id="ax-gv" /><Label htmlFor="ax-gv" className="cursor-pointer">É integrante de um dos Grupos de Visita?</Label></div>
                    {pessoaForm.genero === 'feminino' && <div className="flex items-center space-x-2"><Checkbox checked={pessoaForm.organista} onCheckedChange={(c) => setPessoaForm({ ...pessoaForm, organista: !!c })} id="ax-org" /><Label htmlFor="ax-org" className="cursor-pointer">É organista?</Label></div>}
                    {pessoaForm.genero === 'feminino' && <div className="flex items-center space-x-2"><Checkbox checked={pessoaForm.instrutora_musicalizacao} onCheckedChange={(c) => setPessoaForm({ ...pessoaForm, instrutora_musicalizacao: !!c })} id="ax-im" /><Label htmlFor="ax-im" className="cursor-pointer">É instrutora/auxiliar da aula de musicalização?</Label></div>}
                    {pessoaForm.genero === 'feminino' && <div className="flex items-center space-x-2"><Checkbox checked={pessoaForm.integrante_ebi} onCheckedChange={(c) => setPessoaForm({ ...pessoaForm, integrante_ebi: !!c })} id="ax-ebi" /><Label htmlFor="ax-ebi" className="cursor-pointer">É integrante do EBI (cuida das crianças)?</Label></div>}
                    {pessoaForm.genero === 'masculino' && <div className="flex items-center space-x-2"><Checkbox checked={pessoaForm.instrutor_gem} onCheckedChange={(c) => setPessoaForm({ ...pessoaForm, instrutor_gem: !!c })} id="ax-igem" /><Label htmlFor="ax-igem" className="cursor-pointer">É instrutor de música (GEM)?</Label></div>}
                    {pessoaForm.genero === 'masculino' && <div className="flex items-center space-x-2"><Checkbox checked={pessoaForm.aluno_gem} onCheckedChange={(c) => setPessoaForm({ ...pessoaForm, aluno_gem: !!c })} id="ax-agem" /><Label htmlFor="ax-agem" className="cursor-pointer">É aluno da aula de música (GEM)?</Label></div>}
                    {pessoaForm.genero === 'masculino' && <div className="flex items-center space-x-2"><Checkbox checked={pessoaForm.musico} onCheckedChange={(c) => setPessoaForm({ ...pessoaForm, musico: !!c })} id="ax-mus" /><Label htmlFor="ax-mus" className="cursor-pointer">É músico?</Label></div>}
                    {pessoaForm.genero === 'masculino' && pessoaForm.musico && (
                      <div className="ml-6 p-3 bg-slate-50 rounded-lg border border-slate-200">
                        <p className="text-sm text-slate-600 mb-2">Selecione os instrumentos:</p>
                        <div className="grid grid-cols-2 gap-2">
                          {INSTRUMENTOS.map((inst) => (
                            <div key={inst} className="flex items-center space-x-2">
                              <Checkbox checked={pessoaForm.instrumentos.includes(inst)} onCheckedChange={() => toggleInstrumento(inst)} id={`ax-inst-${inst}`} />
                              <Label htmlFor={`ax-inst-${inst}`} className="cursor-pointer text-sm">{inst}</Label>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                  <Button onClick={handleSavePessoa} className="w-full bg-blue-600 hover:bg-blue-700 mt-4" data-testid="save-pessoa-btn">Salvar</Button>
                </div>
              )}
              {pessoaEditTab === 'anotacoes' && editingPessoa && (
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <h3 className="text-lg font-semibold text-slate-900" style={{ fontFamily: 'Work Sans, sans-serif' }}>Anotações</h3>
                    <Button size="sm" onClick={() => { setEditingAnot(null); setAnotForm({ descricao: '', data: '' }); setAnotDialog(true); }} className="bg-blue-600 hover:bg-blue-700" data-testid="aux-add-anotacao-btn"><Plus className="w-4 h-4 mr-1" />Nova Anotação</Button>
                  </div>
                  <p className="text-sm text-slate-500">Acontecimentos importantes na vida do jovem.</p>
                  <div data-testid="aux-anotacoes-list">
                    {anotLoading ? (
                      <div className="text-center py-8 text-slate-400"><p className="text-sm">Carregando...</p></div>
                    ) : anotacoes.length === 0 ? (
                      <div className="text-center py-8 text-slate-400">
                        <StickyNote className="w-10 h-10 mx-auto mb-2 opacity-50" />
                        <p className="text-sm">Nenhuma anotação registrada</p>
                      </div>
                    ) : anotacoes.map(a => (
                      <div key={a.anotacao_id} className="flex items-center justify-between px-4 py-3 border-b last:border-b-0 border-slate-100 hover:bg-slate-50 transition-colors" data-testid={`aux-anotacao-${a.anotacao_id}`}>
                        <div className="flex items-center gap-3 min-w-0">
                          <div className="w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0"><StickyNote className="w-4 h-4 text-amber-600" /></div>
                          <div className="min-w-0">
                            <p className="text-sm text-slate-900">{a.descricao}</p>
                            {a.data && <span className="flex items-center gap-1 text-xs text-slate-500 mt-0.5"><Calendar className="w-3 h-3" />{fmtDate(a.data)}</span>}
                          </div>
                        </div>
                        <div className="flex gap-1 flex-shrink-0">
                          <Button variant="ghost" size="sm" onClick={() => { setEditingAnot(a); setAnotForm({ descricao: a.descricao, data: a.data || '' }); setAnotDialog(true); }} className="text-blue-600 hover:bg-blue-50"><Edit className="w-4 h-4" /></Button>
                          <Button variant="ghost" size="sm" onClick={() => handleDeleteAnot(a.anotacao_id)} className="text-red-600 hover:bg-red-50"><Trash2 className="w-4 h-4" /></Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* ANOTACAO CREATE/EDIT DIALOG */}
      <Dialog open={anotDialog} onOpenChange={(v) => { setAnotDialog(v); if (!v) { setEditingAnot(null); setAnotForm({ descricao: '', data: '' }); } }}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>{editingAnot ? 'Editar Anotação' : 'Nova Anotação'}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Data</Label>
              <Input type="date" value={anotForm.data} onChange={(e) => setAnotForm({ ...anotForm, data: e.target.value })} data-testid="aux-anotacao-data" />
              <p className="text-xs text-slate-500 mt-1">Opcional. Data do acontecimento.</p>
            </div>
            <div>
              <Label>Descrição *</Label>
              <textarea value={anotForm.descricao} onChange={(e) => setAnotForm({ ...anotForm, descricao: e.target.value })} rows={4} className="flex w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring resize-none" data-testid="aux-anotacao-descricao" />
            </div>
            <Button onClick={handleSaveAnot} className="w-full bg-blue-600 hover:bg-blue-700" data-testid="aux-anotacao-save-btn">
              {editingAnot ? 'Atualizar' : 'Salvar'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Close Event Dialog */}
      <Dialog open={closeDialog} onOpenChange={setCloseDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Fechar Evento</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-slate-600 mb-2">
              Ao fechar o evento <strong>{selectedEvent?.name}</strong>, a presença será finalizada.
            </p>
            {selectedTipoClose && (selectedTipoClose.tem_presidencia || selectedTipoClose.tem_participantes || selectedTipoClose.tem_musicos || selectedTipoClose.tem_ministerio) && (
              <div className="space-y-4 border border-slate-200 rounded-lg p-4">
                <span className="block text-center text-xs font-semibold text-slate-700 bg-slate-100 border border-slate-200 px-2.5 py-1 rounded-full uppercase tracking-wider pointer-events-none">Informações Adicionais</span>
                {selectedTipoClose.tem_presidencia && (
                  <div><Label className="text-xs text-slate-500">Quem Presidiu? *</Label><Input value={closeForm.quem_presidiu} onChange={(e) => setCloseForm({ ...closeForm, quem_presidiu: e.target.value })} data-testid="aux-close-quem-presidiu" /></div>
                )}
                {(selectedTipoClose.tem_musicos || selectedTipoClose.tem_ministerio || selectedTipoClose.tem_participantes) && (
                  <div className={`grid gap-3 ${[selectedTipoClose.tem_musicos, selectedTipoClose.tem_ministerio, selectedTipoClose.tem_participantes].filter(Boolean).length >= 3 ? 'grid-cols-3' : [selectedTipoClose.tem_musicos, selectedTipoClose.tem_ministerio, selectedTipoClose.tem_participantes].filter(Boolean).length === 2 ? 'grid-cols-2' : 'grid-cols-1'}`}>
                    {selectedTipoClose.tem_musicos && (
                      <div><Label className="text-xs text-slate-500">Qtd. de Músicos *</Label><Input type="number" min="0" value={closeForm.qtd_musicos} onChange={(e) => setCloseForm({ ...closeForm, qtd_musicos: e.target.value })} data-testid="aux-close-qtd-musicos" /></div>
                    )}
                    {selectedTipoClose.tem_ministerio && (
                      <div><Label className="text-xs text-slate-500">Qtd. de Ministério *</Label><Input type="number" min="0" value={closeForm.qtd_ministerio} onChange={(e) => setCloseForm({ ...closeForm, qtd_ministerio: e.target.value })} data-testid="aux-close-qtd-ministerio" /></div>
                    )}
                    {selectedTipoClose.tem_participantes && (
                      <div><Label className="text-xs text-slate-500">Qtd. de Participantes *</Label><Input type="number" min="0" value={closeForm.qtd_participantes} onChange={(e) => setCloseForm({ ...closeForm, qtd_participantes: e.target.value })} data-testid="aux-close-qtd-participantes" /></div>
                    )}
                  </div>
                )}
              </div>
            )}
            {/* Recitativos warning */}
            {selectedTipoClose?.tem_recitativos && !closeRecStatus.complete && (
              <div className="border border-amber-200 bg-amber-50 rounded-lg p-3 mt-3 space-y-2" data-testid="close-rec-warning">
                <div className="flex items-center gap-2 text-amber-700">
                  <AlertTriangle className="w-4 h-4 flex-shrink-0" />
                  <p className="text-sm font-medium">Não é possível fechar o evento</p>
                </div>
                <p className="text-xs text-amber-600 ml-6">A contagem de recitativos precisa ser preenchida antes de fechar.</p>
                {closeRecStatus.missingGenders.map((mg, i) => (
                  <div key={i} className="ml-6 bg-transparent rounded p-2 space-y-1">
                    <p className="text-xs font-semibold text-amber-700">
                      {mg.label} — {mg.contadorName ? <>aguardando <strong>{mg.contadorName}</strong></> : 'contador(a) não atribuído'}
                    </p>
                    {mg.missingFuncNames?.length > 0 && (
                      <p className="text-[10px] text-amber-600">Faltam: {mg.missingFuncNames.join(', ')}</p>
                    )}
                  </div>
                ))}
              </div>
            )}
            <Button
              onClick={closeRecStatus.complete ? handleCloseEventSubmit : handleSaveAdditionalInfo}
              disabled={closeSaving}
              className="w-full bg-orange-600 hover:bg-orange-700"
              data-testid="aux-close-event-confirm"
            >
              {closeSaving ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" />Salvando...</> : closeRecStatus.complete ? <><Lock className="w-4 h-4 mr-1.5" />Salvar e Fechar</> : <><Save className="w-4 h-4 mr-1.5" />Salvar</>}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Contagem Dialog */}
      <Dialog open={contagemDialog} onOpenChange={setContagemDialog}>
        <DialogContent className="max-w-md max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-base flex items-center gap-2">
              <Hash className={`w-5 h-5 ${(auxiliar?.genero || '').toLowerCase() === 'feminino' ? 'text-pink-600' : 'text-blue-600'}`} />Contagem de Recitativos
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-slate-600">
              Registre a quantidade de recitativos para <strong>{selectedEvent?.name}</strong>.
            </p>
            {contagemRecFunctions.length === 0 ? (
              <p className="text-sm text-slate-400 italic text-center py-4">Nenhuma função de recitativo encontrada.</p>
            ) : (
              <div className="space-y-3 border border-slate-200 rounded-lg p-4">
                <span className="block text-center text-xs font-semibold text-slate-700 bg-slate-100 border border-slate-200 px-2.5 py-1 rounded-full uppercase tracking-wider pointer-events-none">
                  Todas as contagens
                </span>
                {contagemRecFunctions.map(func => (
                  <div key={func.function_id}>
                    <Label className="text-xs text-slate-500">{func.nome}</Label>
                    <Input
                      type="number"
                      min="0"
                      value={contagemForm[func.function_id] ?? ''}
                      onChange={(e) => setContagemForm(prev => ({ ...prev, [func.function_id]: e.target.value }))}
                      data-testid={`contagem-rec-${func.function_id}`}
                    />
                  </div>
                ))}
              </div>
            )}
            <Button
              onClick={handleSaveContagem}
              disabled={contagemSaving || contagemRecFunctions.length === 0}
              className="w-full bg-blue-600 hover:bg-blue-700"
              data-testid="contagem-save-btn"
            >
              {contagemSaving ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" />Salvando...</> : <><Hash className="w-4 h-4 mr-1.5" />Salvar Contagem</>}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={birthdayCalendarDialog} onOpenChange={setBirthdayCalendarDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Agenda de Aniversários</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-slate-600">Copie este link e adicione no Google Agenda em <strong>Outras agendas → Adicionar por URL</strong>.</p>
            <p className="text-xs text-amber-700 bg-amber-50 border border-amber-100 rounded-lg p-3">A descrição dos eventos inclui as informações completas do cadastro da pessoa.</p>
            <div>
              <Label>Link da agenda</Label>
              <div className="mt-1 flex gap-2">
                <Input value={birthdayCalendarLoading ? 'Gerando link...' : birthdayCalendarUrl} readOnly className="font-mono text-xs" data-testid="aux-birthday-calendar-url-input" />
                <Button onClick={copyBirthdayCalendarUrl} disabled={!birthdayCalendarUrl || birthdayCalendarLoading} data-testid="aux-copy-birthday-calendar-url-btn">Copiar</Button>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Reopen Event Dialog */}
      <Dialog open={reopenDialog} onOpenChange={setReopenDialog}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Reabrir Evento</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-slate-600">
              Deseja reabrir o evento <strong>{selectedEvent?.name}</strong>? A presença poderá ser editada novamente.
            </p>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setReopenDialog(false)} className="flex-1">Cancelar</Button>
              <Button onClick={handleReopenEvent} className="flex-1 bg-green-600 hover:bg-green-700" data-testid="aux-reopen-confirm-btn">Reabrir</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* My Schedules Modal */}
      <Dialog open={mySchedulesOpen} onOpenChange={setMySchedulesOpen}>
        <DialogContent className="max-w-md max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-base flex items-center gap-2">
              <ClipboardCheck className="w-5 h-5 text-orange-600" />Minhas Escalas
            </DialogTitle>
          </DialogHeader>
          {mySchedulesLoading ? (
            <div className="flex items-center justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>
          ) : mySchedules.length === 0 ? (
            <div className="text-center py-8 text-slate-400 text-sm">
              <Calendar className="w-8 h-8 mx-auto mb-2 opacity-40" />
              <p>Nenhuma escala encontrada para os próximos eventos.</p>
            </div>
          ) : (
            <div className="space-y-3">
              {mySchedules.map((s) => {
                const isExpanded = expandedEvent === s.event_id;
                const tipo = tiposEvento.find(t => t.tipo_evento_id === s.tipo_evento_id);
                return (
                  <div key={s.event_id} className="border border-slate-200 rounded-lg overflow-hidden bg-white mt-3 first:mt-0" data-testid={`schedule-event-${s.event_id}`}>
                    <button
                      onClick={() => setExpandedEvent(isExpanded ? null : s.event_id)}
                      className="w-full flex items-center justify-between px-3 py-2.5 bg-white hover:bg-slate-50 transition-colors text-left"
                    >
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <p className="text-sm font-semibold text-slate-800 truncate">{s.event_name}</p>
                          {tipo && <Badge variant="outline" className="text-[10px] flex-shrink-0">{tipo.nome}</Badge>}
                        </div>
                        <p className="text-xs text-slate-500 mt-0.5">{fmtDate(s.event_date)}</p>
                      </div>
                      <div className="flex items-center gap-1.5 flex-shrink-0 ml-2">
                        <Badge className={`text-[10px] ${s.presenca === true ? 'bg-green-100 text-green-700' : s.presenca === false ? 'bg-red-100 text-red-700' : 'bg-slate-100 text-slate-500'}`}>
                          {s.presenca === true ? 'Presente' : s.presenca === false ? 'Ausente' : 'Aguardando'}
                        </Badge>
                        {isExpanded ? <ChevronDown className="w-4 h-4 text-slate-400" /> : <ChevronRight className="w-4 h-4 text-slate-400" />}
                      </div>
                    </button>
                    {isExpanded && (
                      <div className="px-3 pb-3 pt-2 border-t border-slate-100">
                        {(() => {
                          const repLog = s.replacement_log || [];
                          const repMap = {};
                          for (const r of repLog) repMap[r.function_name] = r.replacement_name || '';
                          const activeFuncs = (s.my_functions || []).filter(f => !repMap[f.function_name]);
                          const redirectedFuncs = repLog.map(r => ({ function_name: r.function_name, replacement_name: r.replacement_name || '' }));
                          if (activeFuncs.length === 0 && redirectedFuncs.length === 0) return <p className="text-xs text-slate-400">Sem detalhes adicionais.</p>;
                          return (
                            <div>
                              <p className="text-[11px] font-medium text-slate-500 uppercase tracking-wide mb-1.5">Funções</p>
                              <div className="flex flex-wrap gap-1.5">
                                {activeFuncs.map((f, i) => (
                                  <span key={`active-${i}`} className="relative inline-flex">
                                    <Badge className="bg-blue-50 text-blue-700 border border-blue-200 text-xs">{f.function_name}</Badge>
                                    {f.is_leader && (
                                      <TooltipProvider delayDuration={0}><Tooltip><TooltipTrigger asChild>
                                        <span className="absolute -top-1 -right-1 w-3.5 h-3.5 rounded-full bg-amber-400 flex items-center justify-center ring-1 ring-white"><Star className="w-2 h-2 text-white fill-white" /></span>
                                      </TooltipTrigger><TooltipContent className="max-w-xs"><p className="text-xs">Você é o principal responsável por liderar a fila e escrever os recitativos neste evento.</p></TooltipContent></Tooltip></TooltipProvider>
                                    )}
                                  </span>
                                ))}
                                {redirectedFuncs.map((r, i) => (
                                  <TooltipProvider key={`redir-${i}`} delayDuration={0}>
                                    <Tooltip>
                                      <TooltipTrigger asChild>
                                        <Badge className="bg-amber-50 text-amber-600 border border-amber-200 text-xs line-through opacity-70">{r.function_name}</Badge>
                                      </TooltipTrigger>
                                      <TooltipContent><p className="text-xs">Função redirecionada{r.replacement_name ? ` para ${r.replacement_name}` : ''} por ausência</p></TooltipContent>
                                    </Tooltip>
                                  </TooltipProvider>
                                ))}
                              </div>
                            </div>
                          );
                        })()}
                        <div className="mt-2">
                          <div className="flex items-center justify-between mb-1.5">
                            <p className="text-[11px] font-medium text-slate-500 uppercase tracking-wide">Confirmação de presença</p>
                            <button onClick={() => setLogModal(s)} className="p-1 rounded hover:bg-slate-200 transition-colors" data-testid={`log-btn-${s.event_id}`} title="Histórico">
                              <BookOpen className="w-3.5 h-3.5 text-slate-400" />
                            </button>
                          </div>
                          <div className="flex gap-2">
                            <button
                              onClick={() => handleAbsentClick(s)}
                              className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg text-xs font-semibold transition-colors ${s.presenca === false ? 'bg-red-600 text-white' : 'bg-slate-100 text-slate-500 hover:bg-slate-200'}`}
                              data-testid={`mark-absent-${s.event_id}`}
                            >
                              <Circle className="w-3.5 h-3.5" /> Ausente
                            </button>
                            <button
                              onClick={() => {
                                if (s.presenca === true) {
                                  markPresenca(s.event_id, s.pessoa_id, null);
                                } else {
                                  markPresenca(s.event_id, s.pessoa_id, true);
                                }
                              }}
                              className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg text-xs font-semibold transition-colors ${s.presenca === true ? 'bg-green-600 text-white' : 'bg-slate-100 text-slate-500 hover:bg-green-50'}`}
                              data-testid={`mark-present-${s.event_id}`}
                            >
                              <CheckCircle2 className="w-3.5 h-3.5" /> Presente
                            </button>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!absenceConfirm} onOpenChange={(open) => { if (!open) setAbsenceConfirm(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmar ausência</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="text-sm text-slate-600">
                <p>Você está prestes a se marcar como <strong className="text-red-600">ausente</strong> no evento <strong>{absenceConfirm?.eventName}</strong>.{absenceConfirm?.functions?.length > 0 && <> Você está escalado para: <strong>{absenceConfirm.functions.map(f => f.function_name).join(', ')}</strong>.</>} Ao confirmar, você será automaticamente substituído nas funções acima.</p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={confirmAbsence} className="bg-red-600 hover:bg-red-700">Confirmar ausência</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={!!logModal} onOpenChange={(open) => { if (!open) setLogModal(null); }}>
        <DialogContent className="max-w-xl max-h-[85vh]">
          <DialogHeader>
            <DialogTitle className="text-base">Histórico — {logModal?.event_name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-1.5 max-h-[60vh] overflow-y-auto pr-1">
            {logModal?.historico?.length > 0 ? (
              <>
                {logModal.historico.map((entry, i) => {
                  const isPresente = entry.includes('Registrou Presente');
                  const isAusente = entry.includes('Registrou Ausente');
                  const isDesmarcou = entry.includes('Desmarcou');
                  const isSubstituicao = entry.includes('Substituido em') || entry.includes('Sem substituto');
                  let icon, iconColor;
                  if (isPresente) { icon = <CheckCircle2 className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />; }
                  else if (isAusente) { icon = <Circle className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" />; }
                  else if (isDesmarcou) { icon = <X className="w-4 h-4 text-amber-500 mt-0.5 flex-shrink-0" />; }
                  else if (isSubstituicao) { icon = <Users className="w-4 h-4 text-blue-500 mt-0.5 flex-shrink-0" />; }
                  else { icon = <Info className="w-4 h-4 text-slate-400 mt-0.5 flex-shrink-0" />; }
                  return (
                    <div key={i} className="flex items-start gap-2 text-sm">
                      {icon}
                      <p className="text-slate-700">{entry}</p>
                    </div>
                  );
                })}
              </>
            ) : (
              <p className="text-sm text-slate-400">Nenhuma ação registrada ainda.</p>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Modal Escalas (somente leitura) */}
      {selectedEvent && (
        <EscalasAuxView
          open={escalasViewOpen}
          onClose={() => setEscalasViewOpen(false)}
          eventId={selectedEvent.event_id}
          pessoas={pessoas}
          auxGender={(auxiliar?.genero || '').toLowerCase()}
        />
      )}

      <HelpWizardModal open={helpOpen} onClose={() => setHelpOpen(false)} />

      {/* Modal confirmação de alteração recente */}
      <AlertDialog open={!!recentChangeConfirm} onOpenChange={(v) => { if (!v) setRecentChangeConfirm(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Alteração Recente</AlertDialogTitle>
            <AlertDialogDescription>
              <strong>{recentChangeConfirm?.markedBy}</strong> alterou o status desta pessoa há <strong>{recentChangeConfirm?.secondsAgo}s</strong>. Deseja continuar mesmo assim?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setRecentChangeConfirm(null)}>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={() => { const { pessoaId, desiredPresente } = recentChangeConfirm; setRecentChangeConfirm(null); executeToggleAttendance(pessoaId, desiredPresente); }}>Continuar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Footer />
    </div>
  );
}

function AttendanceRow({ pessoa, presente, onToggle, absentLast5 = [] }) {
  const isPresente = presente === true;

  return (
    <div className={`flex items-center gap-3 sm:gap-4 px-3 sm:px-4 py-2.5 border-b last:border-b-0 transition-all ${isPresente ? 'border-green-100 bg-green-50/30' : 'border-slate-100'}`} data-testid={`attendance-row-${pessoa.pessoa_id}`}>
      <PessoaAvatar pessoa={pessoa} testId={`attendance-pessoa-photo-${pessoa.pessoa_id}`} />
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <p className="font-semibold text-sm text-slate-900 truncate">{pessoa.nome_completo}</p>
          {absentLast5.includes(pessoa.pessoa_id) && (
            <TooltipProvider delayDuration={0}><Tooltip><TooltipTrigger asChild><AlertTriangle className="w-4 h-4 text-amber-500 flex-shrink-0" /></TooltipTrigger><TooltipContent><p>Ausente nos últimos 5 eventos</p></TooltipContent></Tooltip></TooltipProvider>
          )}
        </div>
        <div className="flex items-center gap-1.5 flex-wrap mt-1">
          <Badge variant="outline" className="text-xs">{calcAgeLabel(pessoa.data_nascimento)}</Badge>
          {pessoa.organista && <Badge variant="outline" className="text-xs" title="Organista"><Piano className="w-3.5 h-3.5" /></Badge>}
          {pessoa.musico && <Badge variant="outline" className="text-xs" title="Musico"><Music2 className="w-3.5 h-3.5" /></Badge>}
        </div>
      </div>
      <button onClick={() => onToggle(pessoa.pessoa_id, !isPresente)} data-testid={`toggle-att-${pessoa.pessoa_id}`}
        className={`flex items-center gap-1 sm:gap-1.5 px-2 sm:px-3 py-1.5 rounded-lg text-xs sm:text-sm font-medium transition-colors flex-shrink-0 ${isPresente ? 'bg-green-600 text-white hover:bg-green-700' : 'bg-slate-200 text-slate-700 hover:bg-slate-300'}`}>
        {isPresente ? <><CheckCircle2 className="w-4 h-4" /><span className="hidden sm:inline">Presente</span></> : <><Circle className="w-4 h-4" /><span className="hidden sm:inline">Ausente</span></>}
      </button>
    </div>
  );
}

function PessoaRow({ pessoa, onEdit, onAnotacoes }) {
  return (
    <div className="flex items-center gap-3 sm:gap-4 px-3 sm:px-4 py-2.5 border-b border-slate-100 last:border-b-0" data-testid={`pessoa-row-${pessoa.pessoa_id}`}>
      <PessoaAvatar pessoa={pessoa} testId={`aux-pessoa-list-photo-${pessoa.pessoa_id}`} />
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <p className="font-semibold text-sm text-slate-900 truncate">{pessoa.nome_completo}</p>
        </div>
        <div className="flex items-center gap-1.5 flex-wrap mt-1">
          <Badge variant="outline" className="text-xs">{calcAgeLabel(pessoa.data_nascimento)}</Badge>
          {pessoa.organista && <Badge variant="outline" className="text-xs" title="Organista"><Piano className="w-3.5 h-3.5" /></Badge>}
          {pessoa.musico && <Badge variant="outline" className="text-xs" title="Musico"><Music2 className="w-3.5 h-3.5" /></Badge>}
        </div>
      </div>
      <div className="flex gap-0.5 flex-shrink-0">
        <Button variant="ghost" size="icon" className="h-8 w-8 text-amber-600 hover:bg-amber-50" onClick={() => onAnotacoes(pessoa)} data-testid={`anot-pessoa-${pessoa.pessoa_id}`} title="Anotações">
          <StickyNote className="w-4 h-4" />
        </Button>
        <Button variant="ghost" size="icon" className="h-8 w-8 text-blue-600 hover:bg-blue-50" onClick={() => onEdit(pessoa)} data-testid={`edit-pessoa-${pessoa.pessoa_id}`} title="Editar">
          <Edit className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );
}
